import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

const en = {
  "beta.banner": "Beta: test money only. No real funds move yet.",

  "nav.home": "Home",
  "nav.circles": "Circles",
  "nav.pay": "Pay",
  "nav.campaigns": "Fundraising",
  "nav.me": "Me",
  "nav.activity": "Activity",
  "nav.signIn": "Sign in",
  "nav.getStarted": "Get started",
  "nav.openApp": "Open the app",

  "join.lede":
    "Run your tontine on CafonaPay. Everyone pays in, one member receives the whole pot, and the turn moves on, tracked to the cent and visible to the whole circle.",
  "landing.footer": "CafonaPay is a payment and financial management platform, in beta. Balances are test money until our licensed payment partners are in place.",

  "auth.signIn": "Sign in",
  "auth.signUp": "Create your account",
  "auth.email": "Email",
  "auth.contact": "Email or phone number",
  "auth.contactHint": "Use your email, or your mobile number. Numbers from another country start with + and the country code.",
  "auth.country": "Country",
  "errors.generic": "Something went wrong. Try again.",
  "errors.invalid_request": "Check the highlighted fields.",
  "errors.invalid_credentials": "That email or phone number and password don't match.",
  "errors.contact_required": "Enter your email or phone number.",
  "errors.invalid_email": "Enter a valid email address.",
  "errors.invalid_phone": "Enter a valid phone number. For another country, start with + and the country code.",
  "errors.email_taken": "An account already uses this email. Sign in instead.",
  "errors.phone_taken": "An account already uses this phone number. Sign in instead.",
  "errors.handle_taken": "That @handle is taken. Try another.",
  "errors.invalid_handle": "Use 3 to 20 letters, numbers or underscores.",
  "errors.password_too_short": "Use at least 10 characters.",
  "errors.too_short": "This is too short.",
  "errors.required": "This is required.",
  "errors.too_many_attempts": "Too many attempts. Wait 15 minutes and try again.",
  "errors.offline": "You're offline. Check your connection and try again.",
  "errors.insufficient_funds": "Your balance is too low for this. Add money to your wallet first.",
  "errors.not_found_user": "No CafonaPay user with that @handle, email or phone number.",
  "errors.internal": "Something went wrong on our side. Try again in a moment.",
  "auth.password": "Password",
  "auth.name": "Full name",
  "auth.handle": "Choose a @handle",
  "auth.handleHint": "Friends use it to send you money.",
  "auth.passwordHint": "At least 10 characters.",
  "auth.currency": "Main currency",
  "auth.noAccount": "New to CafonaPay?",
  "auth.haveAccount": "Already have an account?",
  "auth.signOut": "Sign out",

  "home.add": "Add money",
  "home.send": "Send",
  "home.request": "Request",
  "home.withdraw": "Withdraw",
  "home.circles": "Your circles",
  "home.circles.empty": "You're not in a circle yet. Start one with the people you trust, or join with an invite code.",
  "home.recent": "Recent activity",
  "home.activity.empty": "Nothing here yet. Add money to get started.",
  "home.seeAll": "See all",

  "circles.title": "Circles",
  "circles.new": "Start a circle",
  "circles.join": "Join with a code",
  "circles.code": "Invite code",
  "circles.empty": "No circles yet.",
  "circles.turnOf": "Turn {n} of {total}",
  "circles.potFor": "Pot for {name}",
  "circles.yourTurn": "Your turn",
  "circles.forming": "Gathering members",
  "circles.active": "In progress",
  "circles.completed": "Completed",
  "circles.cancelled": "Cancelled",
  "circles.youPaid": "You've paid this turn",
  "circles.youOwe": "Your payment is due",
  "circles.members": "{n} of {total} members",

  "circle.name": "Circle name",
  "circle.namePlaceholder": "Car fund, Family njangi…",
  "circle.description": "What's it for? (optional)",
  "circle.amount": "Each member pays",
  "circle.frequency": "How often",
  "circle.weekly": "Every week",
  "circle.biweekly": "Every two weeks",
  "circle.monthly": "Every month",
  "circle.size": "Number of members",
  "circle.start": "First payment date",
  "circle.order": "Order of turns",
  "circle.order.join_order": "In the order people join",
  "circle.order.random": "Random draw when it starts",
  "circle.order.manual": "I'll set it myself",
  "circle.summary": "Each turn, the pot is {pot}. The circle runs {n} turns, ending {end}.",
  "circle.create": "Create circle",
  "circle.invite": "Invite members",
  "circle.inviteBody": "Share this link or code. People join until the circle is full.",
  "circle.copy": "Copy link",
  "circle.copied": "Link copied",
  "circle.share": "Share",
  "circle.startNow": "Start the circle",
  "circle.startHint": "Starting locks the members and the order of turns.",
  "circle.pay": "Pay {amount} for this turn",
  "circle.paid": "Paid",
  "circle.waiting": "Not yet",
  "circle.received": "Received",
  "circle.schedule": "Schedule",
  "circle.thisTurn": "This turn",
  "circle.due": "Due {date}",
  "circle.collected": "{collected} of {pot} collected",
  "circle.payoutDone": "Pot released to {name}",
  "circle.moveUp": "Move up",
  "circle.moveDown": "Move down",
  "circle.saveOrder": "Save order",
  "circle.leave": "Leave circle",
  "circle.cancel": "Cancel circle",
  "circle.organizer": "Organizer",
  "circle.you": "you",
  "circle.completedNote": "Every member has had their turn. This circle is complete.",
  "circle.joinTitle": "{name} invited you to a circle",
  "circle.joinCta": "Join this circle",
  "circle.joinDetails": "{amount} {frequency} · {joined} of {total} joined",
  "circle.startsOn": "First payment {date}",

  "pay.title": "Pay",
  "pay.send": "Send money",
  "pay.request": "Request money",
  "pay.to": "To",
  "pay.toPlaceholder": "@handle, email or phone number",
  "pay.amount": "Amount",
  "pay.currency": "Currency",
  "pay.note": "Note (optional)",
  "pay.notePlaceholder": "What's it for?",
  "pay.review": "Review",
  "pay.confirmSend": "Send {amount}",
  "pay.sent": "{amount} sent to {name}",
  "pay.from": "Ask a specific person (optional)",
  "pay.fromHint": "Leave empty and anyone with the link can pay.",
  "pay.createRequest": "Create payment link",
  "pay.requestReady": "Your payment link is ready",
  "pay.requestsSent": "Your payment links",
  "pay.requestsEmpty": "Links you create to get paid back will show here, with who has paid.",
  "pay.payRequest": "Pay {amount}",
  "pay.requestFrom": "{name} is asking for",
  "pay.requestPaid": "This request has been paid.",
  "pay.requestCancelled": "This request was cancelled.",
  "pay.cancelRequest": "Cancel link",
  "pay.open": "Open",
  "pay.paidStatus": "Paid",
  "pay.cancelledStatus": "Cancelled",

  "wallet.add": "Add money",
  "wallet.addBody": "Test mode: money is added instantly and isn't real.",
  "wallet.withdrawBody": "Test mode: the withdrawal is simulated and no real money moves. In live mode, money goes to the bank account or mobile money number saved on your profile.",
  "wallet.method": "Pay with",
  "wallet.method.interac": "Interac e-Transfer",
  "wallet.method.card": "Debit or credit card",
  "wallet.method.mobile_money": "Mobile money",
  "wallet.method.bank": "Bank transfer",
  "wallet.addCta": "Add {amount}",
  "wallet.added": "{amount} added to your wallet",
  "wallet.withdraw": "Withdraw",
  "wallet.withdrawCta": "Withdraw {amount}",
  "wallet.withdrawn": "{amount} on its way",
  "wallet.available": "{amount} available",

  "campaigns.title": "Fundraising",
  "campaigns.new": "Start a fundraiser",
  "campaigns.empty": "Fundraisers you start or support will appear here.",
  "campaign.title": "Title",
  "campaign.story": "Tell the story",
  "campaign.storyHint": "Why are you raising money, and what will it pay for?",
  "campaign.goal": "Goal",
  "campaign.endsOn": "End date (optional)",
  "campaign.create": "Publish fundraiser",
  "campaign.raised": "{raised} raised of {goal}",
  "campaign.backers": "{n} supporters",
  "campaign.give": "Give",
  "campaign.giveCta": "Give {amount}",
  "campaign.message": "Message (optional)",
  "campaign.anonymous": "Hide my name",
  "campaign.thanks": "Thank you. Your {amount} is in.",
  "campaign.withdraw": "Move {amount} to my wallet",
  "campaign.close": "Close fundraiser",
  "campaign.closed": "This fundraiser is closed.",
  "campaign.by": "Organized by {name}",
  "campaign.recent": "Recent support",
  "campaign.signInToGive": "Sign in to give",
  "campaign.share": "Share fundraiser",

  "me.title": "Your account",
  "me.language": "Language",
  "me.currency": "Main currency",
  "me.save": "Save changes",
  "me.saved": "Saved",
  "me.install": "Install the app",
  "me.installBody": "Add CafonaPay to your home screen for one-tap access.",
  "me.about": "About this beta",

  "nav.community": "Community",
  "common.copy": "Copy",
  "wallet.addBodyCheckout": "You'll confirm the payment on the next screen. Your wallet is credited as soon as the payment goes through.",
  "wallet.withdrawTo": "Send to",
  "co.yourWallet": "Your CafonaPay wallet",
  "co.topUpSub": "Add money",
  "co.verified": "Verified",
  "co.testBanner": "Test mode: no real money moves. Test cards and codes are shown on each step.",
  "co.secured": "Secure payment · encrypted connection",
  "co.chooseMethod": "Choose how to pay",
  "co.card": "Card",
  "co.mobileMoney": "Mobile money",
  "co.wallet": "CafonaPay balance",
  "co.walletSignIn": "Sign in to pay with your balance",
  "co.mmUnavailable": "Not available in {currency}",
  "co.operator": "Operator",
  "co.mmNumber": "Mobile money number",
  "co.pay": "Pay {amount}",
  "co.tryAgain": "Try again or choose another method.",
  "co.backToMerchant": "Return to the site",
  "co.otherMethod": "Other payment methods",
  "co.cardName": "Name on card",
  "co.cardNumber": "Card number",
  "co.expiry": "Expiry (MM/YY)",
  "co.testCards": "Test cards",
  "co.approveTitle": "Approve on your phone",
  "co.approveBody": "{phone} will receive a request from your operator. Approve it, then enter the code you receive.",
  "co.code": "Confirmation code",
  "co.testCode": "Test code: {code}",
  "co.confirm": "Confirm payment",
  "co.confirming": "Confirming your payment…",
  "co.confirmingBody": "This usually takes a few seconds. You can keep this page open.",
  "co.success": "Payment complete",
  "co.paidWith": "Paid with",
  "co.date": "Date",
  "co.reference": "Reference",
  "co.toWallet": "Go to my wallet",
  "co.expired": "This payment link has expired",
  "co.canceled": "This payment was cancelled",
  "co.closedBody": "Nothing was charged. Ask for a new payment link if you still want to pay.",
  "pstatus.requires_payment_method": "Waiting",
  "pstatus.requires_action": "In progress",
  "pstatus.processing": "Processing",
  "pstatus.succeeded": "Paid",
  "pstatus.failed": "Failed",
  "pstatus.canceled": "Cancelled",
  "pstatus.expired": "Expired",
  "org.title": "Your organizations",
  "org.new": "Create an organization",
  "org.newBody": "An organization collects money for a group: an association, a church, a family fund, a tontine or a small business. It gets its own balance, members with roles, and payment links.",
  "org.empty": "Collect dues, offerings and contributions with payment links, card and mobile money.",
  "org.name": "Organization name",
  "org.namePlaceholder": "Association des Camerounais d'Ottawa",
  "org.kind": "Type",
  "org.create": "Create organization",
  "org.kind.association": "Association",
  "org.kind.church": "Church",
  "org.kind.mosque": "Mosque",
  "org.kind.tontine": "Tontine group",
  "org.kind.family": "Family fund",
  "org.kind.business": "Business",
  "org.kind.school": "School",
  "org.kind.other": "Other",
  "org.role.owner": "Owner",
  "org.role.admin": "Admin",
  "org.role.treasurer": "Treasurer",
  "org.role.member": "Member",
  "org.roleHelp.member": "Members see the organization and its activity.",
  "org.roleHelp.treasurer": "Treasurers create payment links and pay out funds to members.",
  "org.roleHelp.admin": "Admins also manage members, API keys and webhooks.",
  "org.unverified": "Not yet verified",
  "org.balance": "Organization balance",
  "org.tab.overview": "Overview",
  "org.tab.members": "Members",
  "org.tab.developers": "Developers",
  "org.received": "Received (net)",
  "org.payments": "Payments",
  "org.fees": "Fees",
  "org.fee": "fee",
  "org.getPaid": "Get paid",
  "org.getPaidBody": "Create a link anyone can pay by card, mobile money or CafonaPay balance. Share it on WhatsApp, email or a poster.",
  "org.linkFor": "What is it for?",
  "org.linkForPlaceholder": "Annual dues, Sunday offering…",
  "org.createLink": "Create payment link",
  "org.linkReady": "Your payment link is ready",
  "org.anotherLink": "Create another link",
  "org.payout": "Pay out to a member",
  "org.payoutBody": "Move money from the organization to a member's CafonaPay wallet. Every payout is recorded with who approved it.",
  "org.payoutTo": "Member",
  "org.payoutToHint": "Leave empty to pay out to yourself.",
  "org.payoutCta": "Pay out",
  "org.recent": "Payments",
  "org.noPayments": "Payments to this organization will appear here.",
  "org.addMember": "Add a member",
  "org.memberAdded": "Member added",
  "dev.keys": "API keys",
  "dev.keysBody": "Use a secret key on your server to create checkout sessions. Never put it in a website or app.",
  "dev.keyLabel": "Key name",
  "dev.keyLabelPlaceholder": "Website",
  "dev.createKey": "Create key",
  "dev.keyOnce": "Copy your secret key now. For your security it won't be shown again.",
  "dev.secretOnce": "Copy your webhook signing secret now. It won't be shown again.",
  "dev.lastUsed": "last used {date}",
  "dev.neverUsed": "never used",
  "dev.revoke": "Revoke",
  "dev.revoked": "Revoked",
  "dev.webhooks": "Webhooks",
  "dev.webhooksBody": "CafonaPay notifies your server when a payment succeeds or fails, signed so you can verify it came from us.",
  "dev.endpoint": "Endpoint URL",
  "dev.addEndpoint": "Add endpoint",
  "dev.sendTest": "Send a test event",
  "dev.quickstart": "Quickstart",
  "dev.quickstartBody": "Create a checkout session from your server, then send the payer to the returned url.",
  "dev.signatureNote": "Webhooks carry a CafonaPay-Signature header (HMAC-SHA256). Verify it before trusting an event.",
  "dev.docs": "Full API documentation",
  "notif.title": "Notifications",
  "notif.empty": "You're all caught up. Reminders and payment alerts will show here.",
  "notif.unread": "{n} unread notifications",
  "circle.late": "Late",
  "circle.lateNote": "This turn is past its due date. Members have a {days}-day grace period before payments count as overdue.",
  "circle.overdueNote": "This turn is overdue: {n} of {total} members have paid. The pot is held until everyone pays.",
  "circle.remind": "Remind unpaid members",
  "circle.reminded": "Reminder sent to {n} member(s)",
  "errors.invalid_card": "Check the card number.",
  "errors.invalid_expiry": "Use the format MM/YY.",
  "errors.invalid_cvc": "Enter the 3 or 4 digits on the back of the card.",
  "errors.test_card_required": "Test mode: use one of the test cards shown below.",
  "errors.card_expired": "This card has expired.",
  "errors.invalid_otp": "That code isn't right. Check the message from your operator.",
  "errors.invalid_network": "Choose your mobile money operator.",
  "errors.not_member": "Funds can only be paid out to members of the organization.",
  "common.cancel": "Cancel",
  "common.back": "Back",
  "confirm.yes": "Confirm",
  "seo.home.title": "CafonaPay · Payments and money management for communities",
  "seo.home.description": "Plan your money, track receipts item by item, run tontine circles and fundraisers, accept cards and mobile money. For communities in Canada and Africa.",
  "seo.terms.title": "Terms of use · CafonaPay",
  "seo.terms.description": "The terms for using CafonaPay, the payment and financial management platform, during its beta.",
  "seo.privacy.title": "Privacy policy · CafonaPay",
  "seo.privacy.description": "What personal information CafonaPay collects, why, and how you control it (PIPEDA and Quebec Law 25).",
  "seo.dev.title": "Payments API for developers · CafonaPay",
  "seo.dev.description": "Accept cards and mobile money through one API: checkout sessions, payments, refunds and signed webhooks, in CAD and CFA francs.",
  "seo.verify.title": "Verify a CafonaPay receipt",
  "seo.verify.description": "Check that a CafonaPay receipt is genuine: every receipt is sealed in a public hash chain that anyone can verify.",
  "subnav.finances": "My finances",
  "subnav.proof": "Proof of funds",
  "fin.title": "My finances",
  "fin.lede": "Your bank accounts in one place, read-only. You sign in on your bank's or the aggregator's own screen: CafonaPay never sees or stores your banking password.",
  "fin.noAdvice": "CafonaPay organizes your information. It doesn't give personalized investment advice.",
  "fin.net": "Net worth",
  "fin.assets": "What you have",
  "fin.debts": "What you owe",
  "fin.banks": "Linked accounts",
  "fin.connect": "Link an account",
  "fin.simulated": "Test mode: these institutions are simulated and show demo data. In production you sign in through Flinks or Plaid.",
  "fin.pick": "Choose your institution",
  "fin.connected": "Linked: {accounts} accounts and {transactions} transactions imported.",
  "fin.synced": "Up to date.",
  "fin.disconnected": "Disconnected. Those accounts left your plan.",
  "fin.empty": "No account linked yet. Link one to see your spending and balances update on their own.",
  "fin.privacy": "Read-only access. You can disconnect at any time.",
  "fin.readOnly": "Read-only",
  "fin.lastSync": "updated {date}",
  "fin.demo": "demo data",
  "fin.sync": "Update",
  "fin.disconnect": "Disconnect",
  "fin.disconnectPrompt": "Disconnect {name}? Its accounts leave your plan; you can link it again later.",
  "fin.budgets": "Budgets this month",
  "fin.budgets.empty": "No spending recorded this month yet.",
  "fin.limit": "Monthly limit",
  "fin.setLimit": "Set a limit",
  "fin.editLimit": "Change the limit",
  "fin.addBudget": "Add a budget for…",
  "fin.goals": "Goals",
  "fin.goals.empty": "Set a goal: an emergency fund, school fees, a trip home.",
  "fin.addGoal": "Add a goal",
  "fin.goal.name": "Goal",
  "fin.goal.namePh": "Emergency fund",
  "fin.goal.target": "Target",
  "fin.goal.date": "By (optional)",
  "fin.goal.account": "Track with",
  "fin.goal.manual": "I'll update it myself",
  "fin.goal.by": "by {date}",
  "fin.goal.deletePrompt": "Delete the goal {name}?",
  "pof.title": "Proof of funds",
  "pof.lede": "Organize your statements and balance history for an Immigration Canada application, and export a summary.",
  "pof.notCertified": "Not certified. This file organizes your own documents; it doesn't certify anything. Immigration Canada asks for an official letter from each financial institution.",
  "pof.period": "Period",
  "pof.months": "{n} months",
  "pof.export": "Export the summary (PDF)",
  "pof.summary": "Summary",
  "pof.total": "Available today",
  "pof.average": "Average over {n} months",
  "pof.accounts": "Balances at each month end",
  "pof.account": "Account",
  "pof.totalRow": "Total ({currency})",
  "pof.monthEnds": "Total at each month end",
  "pof.noAccounts": "Add your bank accounts in the money plan or link them in My finances.",
  "pof.testMoney": "CafonaPay balances are test money during the beta and are left out of this file.",
  "pof.documents": "Documents",
  "pof.noDocs": "No document yet. Add your statements and your institutions' letters.",
  "pof.kind": "Type",
  "pof.kind.bank_statement": "Bank statement",
  "pof.kind.investment_statement": "Investment statement",
  "pof.kind.gift_letter": "Gift letter",
  "pof.kind.property": "Property document",
  "pof.kind.other": "Other",
  "pof.docTitle": "Title",
  "pof.docTitlePh": "Desjardins letter, May 2027",
  "pof.institution": "Institution (optional)",
  "pof.from": "From",
  "pof.to": "To",
  "pof.file": "File (PDF or photo, 10 MB max)",
  "pof.add": "Add the document",
  "pof.added": "Document added.",
  "pof.deletePrompt": "Delete {title}?",
  "pof.irccNote": "Check the amounts and documents Immigration Canada requires for your program.",
  "pof.irccLink": "IRCC: proof of funds",
  "pof.pdf.badge": "NOT CERTIFIED — DOCUMENT ORGANIZED BY THE HOLDER",
  "pof.pdf.badgeSub": "This summary is not an official document and is not certified by CafonaPay or any financial institution.",
  "pof.pdf.title": "Proof-of-funds file: organized summary",
  "pof.pdf.generated": "generated on {date}",
  "pof.pdf.period": "Period: {from} to {to}",
  "pof.pdf.ircc": "Immigration Canada requires an official letter from each financial institution. Attach those letters to your application.",
  "pof.pdf.footer": "CafonaPay · payment and financial management platform · Not certified: organized from information entered or linked by the holder.",
  "common.copied": "Copied",
  "circle.mode": "How members pay",
  "circle.mode.coordination": "Members pay each other",
  "circle.mode.coordination.body": "Each turn, members send their share directly to that turn's member by Interac. CafonaPay keeps the schedule, reminders and confirmations, and never holds the money.",
  "circle.mode.wallet": "Pot in CafonaPay (test money)",
  "circle.mode.wallet.body": "Members pay into a pot that is released automatically. Test money only, until a licensed partner holds the funds.",
  "circle.thisTurnChip": "This turn",
  "circle.confirmed": "Confirmed",
  "circle.declared": "Declared",
  "circle.reliability": "{score}% reliable",
  "circle.reliability.help": "On time: {onTime} · late: {late} · missed: {missed}",
  "circle.declaredToast": "Declared. {name} will confirm when it arrives.",
  "circle.confirmedToast": "Payment confirmed.",
  "circle.disputedToast": "Marked as not received. They've been told.",
  "circle.contactSaved": "Saved. Members will see it on your turn.",
  "circle.contact": "Where you receive your turn",
  "circle.contactHint": "Your Interac e-mail or phone. Only this circle's members see it.",
  "circle.coord.yourTurn": "Your turn: {amount}",
  "circle.coord.yourTurnBody": "{n} members each send you {amount} directly. Confirm each payment when it reaches your account.",
  "circle.coord.addContact": "Add where you want to receive the money so members know where to send it.",
  "circle.coord.says": "{name} says they sent {amount}",
  "circle.ref": "ref. {ref}",
  "circle.coord.received": "I received it",
  "circle.coord.notReceived": "Not received",
  "circle.coord.confirm": "Confirm",
  "circle.coord.dispute": "Mark not received",
  "circle.coord.confirmPrompt": "Confirm you received {amount} from {name}? This can't be undone.",
  "circle.coord.disputePrompt": "Tell {name} the money hasn't arrived? They can declare it again.",
  "circle.coord.send": "Send {amount} to {name}",
  "circle.coord.to": "Interac to",
  "circle.coord.noContact": "{name} hasn't added where to receive the money yet. Ask them in the group.",
  "circle.coord.due": "Due {date}",
  "circle.coord.confirmedMine": "{name} confirmed your payment.",
  "circle.coord.waitingConfirm": "Declared. Waiting for {name} to confirm.",
  "circle.coord.disputedMine": "{name} hasn't received your payment. Check the transfer, then declare it again.",
  "circle.coord.how": "How you paid",
  "circle.coord.reference": "Reference (optional)",
  "circle.coord.referenceHint": "The Interac reference number helps them find it.",
  "circle.coord.iSent": "I sent {amount}",
  "circle.coord.myContact": "Where you receive your own turn",
  "circle.method.interac": "Interac e-Transfer",
  "circle.method.mobile_money": "Mobile money",
  "circle.method.cash": "Cash",
  "circle.method.other": "Other",
  "adm.tab.overview": "Overview",
  "adm.tab.releases": "Releases",
  "adm.tab.docs": "Documentation",
  "adm.tab.features": "Features & compliance",
  "adm.tab.bank": "Banking path",
  "adm.tab.roadmap": "Roadmap",
  "adm.env.staging": "Staging",
  "adm.env.production": "Production",
  "adm.rel.lede": "Every successful deployment to this environment ({env}), recorded by the deploy workflow.",
  "adm.rel.empty": "No deployment recorded yet. The next one will appear here.",
  "adm.rel.pr": "Pull request",
  "adm.rel.run": "CI run",
  "adm.rel.ciOk": "CI passed",
  "adm.docs.search": "Search the documentation",
  "adm.docs.none": "No page matches.",
  "adm.lvl.N0_sans_agrement": "N0 · No licence",
  "adm.lvl.N1_partenaire": "N1 · Licensed partner",
  "adm.lvl.N2_agrement_propre": "N2 · Own licences",
  "adm.lvl.N3_banque": "N3 · Banking charter",
  "adm.st.idea": "Idea",
  "adm.st.dev": "In development",
  "adm.st.beta": "Beta, test money",
  "adm.st.production": "Production",
  "adm.cost.itemized": "{amount} itemized; the rest is the phase envelope",
  "adm.cost.estimated": "Estimated total",
  "adm.cost.phase": "Covered by the phase budget",
  "adm.filters": "Filters",
  "adm.f.level": "Level",
  "adm.f.regulator": "Regulator",
  "adm.f.status": "Status",
  "adm.f.all": "All",
  "adm.f.count": "{shown} of {total} features · {allowed} allowed in production with real money",
  "adm.guard.help": "Production guard: licensed-level features need a recorded authorization before real money can flow.",
  "adm.guard.ok": "Allowed in production",
  "adm.guard.locked": "Test money only",
  "adm.cost": "Estimated cost",
  "adm.delay": "Estimated time",
  "adm.months": "{n} months",
  "adm.monthsRange": "{min}–{max} months",
  "adm.regulators": "Regulators",
  "adm.verified": "Last checked",
  "adm.more": "Prerequisites, authorization, sources",
  "adm.prereq": "Prerequisites",
  "adm.auth": "Authorization:",
  "adm.auth.none": "Not needed (CafonaPay holds no customer funds).",
  "adm.auth.missing": "None recorded. Real money stays off until a partner reference or licence number is added to the registry.",
  "adm.sources": "Sources",
  "adm.bank.lede": "Long-term options if CafonaPay ever takes deposits itself. Each option is to be validated by a lawyer; figures are estimates.",
  "adm.bank.capital": "Capital and investment",
  "adm.bank.steps": "Steps",
  "adm.bank.lawyer": "To be validated by a lawyer.",
  "adm.bank.realism.faible": "Long shot",
  "adm.bank.realism.moyen": "Possible",
  "adm.bank.realism.eleve": "Most realistic",
  "adm.road.window": "Months {from} to {to}",
  "adm.road.milestones": "Milestones",
  "adm.road.funding": "Funding",
  "adm.road.scenarios": "Pre-seed scenarios ($400,000)",
  "adm.fund.add": "Add a source",
  "adm.fund.deleted": "Funding source removed.",
  "adm.fund.deletePrompt": "Remove this funding source?",
  "adm.fund.upTo": "Up to {amount}",
  "adm.fund.nameFr": "Name (French)",
  "adm.fund.nameEn": "Name (English)",
  "adm.fund.type": "Type",
  "adm.fund.max": "Maximum (CAD, optional)",
  "adm.fund.notes": "Notes",
  "adm.fund.url": "Link",
  "adm.fund.type.pret": "Loan",
  "adm.fund.type.capital": "Equity",
  "adm.fund.type.subvention": "Grant",
  "adm.fund.type.credit_impot": "Tax credit",
  "adm.fund.type.accompagnement": "Support programme",
  "adm.fund.type.autre": "Other",
  "plan.edit": "Edit",
  "plan.editAccount": "Edit account",
  "plan.editItem": "Edit bill or income",
  "plan.form.save": "Save changes",
  "plan.removeAccount": "Remove this account from the plan",
  "plan.removeAccountPrompt": "Remove this account? Its entries stay in your history, but it no longer counts in your plan.",
  "plan.removeItemPrompt": "Remove {name} from your plan?",
  "plan.deleteEntry": "Delete",
  "plan.deleteEntryPrompt": "Delete this entry? The account balance is adjusted back.",
  "plan.entryDeleted": "Entry deleted. The balance was adjusted.",
  "plan.status.setup": "Set up",
  "plan.setupNote": "Add your accounts, pay and bills to see what's safe to spend each day.",
  "co.failFunds": "The card has insufficient funds.",
  "confirm.send": "Send {amount} to {name}? Money sent can't be pulled back.",
  "confirm.revoke": "Revoke this key? Anything using it stops working right away.",
  "confirm.refund": "Refund {amount} to the payer? This can't be undone.",
  "confirm.pay": "Pay {amount} to {name}?",
  "confirm.withdraw": "Withdraw {amount}?",
  "common.continue": "Continue",
  "common.done": "Done",
  "common.loading": "Loading…",
  "common.retry": "Try again",
  "common.offline": "You're offline. Balances will refresh when you reconnect.",
  "common.update": "A new version of CafonaPay is ready.",
  "common.reload": "Update",
  "common.notFound": "This page doesn't exist.",
  "common.goHome": "Go home",

  "landing.developers": "Developers",
  "legal.terms": "Terms of use",
  "legal.privacy": "Privacy policy",
  "legal.consent.before": "By creating an account, you agree to the",
  "legal.consent.and": "and the",
  "verify.title": "Verify a receipt",
  "verify.summary": "Check any CafonaPay receipt or certificate",
  "verify.lede": "Every payment on CafonaPay gets a receipt sealed in a public hash chain. Enter a receipt number, or scan its QR code, to check it hasn't been altered.",
  "verify.idLabel": "Receipt number or link",
  "verify.cta": "Verify",
  "verify.kicker": "CafonaPay payment receipt",
  "verify.valid": "Verified",
  "verify.invalid": "Doesn't check out",
  "verify.number": "Receipt",
  "verify.id": "Receipt ID",
  "verify.pending": "Sealing…",
  "verify.hash": "Fingerprint (SHA-256)",
  "verify.prevHash": "Previous receipt's fingerprint",
  "verify.receipt": "Receipt",
  "verify.check.sealed": "Sealed in the public receipt chain",
  "verify.check.hash": "Receipt details unchanged since it was issued",
  "verify.check.chain": "Linked to the receipt before it: nothing inserted or removed",
  "verify.check.ledger": "Matches the ledger entry, which still balances to zero",
  "cert.print": "Print or save as PDF",
  "cert.short": "Certificate",
  "cert.campaignLink": "Certificate of collection",
  "cert.kicker.campaign": "Certificate of collection · fundraiser",
  "cert.kicker.circle": "Certificate of collection · circle",
  "cert.receipts": "{n} receipts",
  "cert.totalIn": "Collected",
  "cert.totalOut": "Paid out",
  "cert.held": "Still held",
  "cert.empty": "No money has moved yet.",
  "cert.from": "From",
  "cert.to": "To",
  "cert.counterparty": "Who",
  "cert.amount": "Amount",
  "cert.date": "Date",
  "cert.subject.circle": "Circle",
  "cert.subject.campaign": "Fundraiser",
  "cert.subject.org": "Organization",
  "cert.digest": "Certificate fingerprint (SHA-256 of all receipts)",
  "cert.head": "Chain head at issue (receipt #{n})",
  "cert.generated": "Issued",
  "cert.qr": "QR code linking to this certificate",
  "cert.scan": "Scan to check it online",
  "cert.disclaimer": "Receipts are sealed in a SHA-256 hash chain, so any later change is detectable. This is tamper evidence, not a notarized document. Beta: amounts are test money.",
  "security.title": "Security",
  "security.summary": "Two-step sign-in, devices, password",
  "security.code": "6-digit code",
  "security.codeHint": "From your authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…).",
  "security.2fa.title": "Two-step sign-in",
  "security.2fa.body": "Ask for a code from an authenticator app each time you sign in, so a stolen password isn't enough to get into your account.",
  "security.2fa.start": "Turn on two-step sign-in",
  "security.2fa.passwordHint": "Confirm it's you before linking an authenticator app.",
  "security.2fa.step1": "Scan this QR code with your authenticator app, or type the key.",
  "security.2fa.step2": "Enter the 6-digit code it shows.",
  "security.2fa.qr": "QR code to add CafonaPay to your authenticator app",
  "security.2fa.openApp": "Open in your authenticator app",
  "security.2fa.manual": "Or type this key:",
  "security.2fa.confirm": "Confirm and turn on",
  "security.2fa.on": "Two-step sign-in is on",
  "security.2fa.off": "Two-step sign-in is off",
  "security.2fa.enabled": "Two-step sign-in is on",
  "security.2fa.enabledBody": "You'll be asked for a code from your authenticator app every time you sign in.",
  "security.2fa.disable": "Turn off two-step sign-in",
  "security.devices.title": "Where you're signed in",
  "security.devices.this": "This device",
  "security.devices.since": "Signed in {date}",
  "security.devices.seen": "active {date}",
  "security.devices.signOut": "Sign out",
  "security.devices.signOutOthers": "Sign out everywhere else",
  "security.devices.signedOut": "Signed out of {n} other device(s)",
  "security.password.title": "Password",
  "security.password.body": "Changing your password signs you out on every other device.",
  "security.password.current": "Current password",
  "security.password.new": "New password",
  "security.password.cta": "Change password",
  "security.password.changed": "Password changed",
  "security.log.title": "Security activity",
  "security.log.empty": "Nothing yet.",
  "audit.auth.login": "Signed in",
  "audit.auth.login_failed": "Failed sign-in attempt",
  "audit.auth.signup": "Account created",
  "audit.auth.2fa_enabled": "Two-step sign-in turned on",
  "audit.auth.2fa_disabled": "Two-step sign-in turned off",
  "audit.auth.password_changed": "Password changed",
  "audit.auth.session_revoked": "Signed out a device",
  "audit.auth.sessions_revoked_others": "Signed out all other devices",
  "admin.title": "Admin console",
  "admin.summary": "Reconciliation, activity, audit trail",
  "admin.reconcile": "Books reconciliation",
  "admin.balanced": "Books balance",
  "admin.unbalanced": "Needs attention",
  "admin.recheck": "Check again",
  "admin.check.zero": "Double entry: every currency sums to zero",
  "admin.check.drift": "Account balances match their postings",
  "admin.check.entries": "Every journal entry balances",
  "admin.check.negative": "No member wallet below zero",
  "admin.check.chain": "Receipt chain intact",
  "admin.chainCount": "{sealed} sealed, {pending} pending",
  "admin.users": "Members",
  "admin.orgs": "Organizations",
  "admin.entries": "Ledger entries",
  "admin.volume": "Volume by currency",
  "admin.payments": "Checkout payments",
  "admin.audit": "Audit trail",
  "errors.totp_required": "Enter the 6-digit code from your authenticator app.",
  "errors.invalid_code": "That code didn't work. Codes change every 30 seconds.",
  "errors.invalid_password": "Your current password isn't right.",
  "errors.totp_already_enabled": "Two-step sign-in is already on.",
  "errors.totp_not_setup": "Start the setup first.",
  "brand.tagline": "Every payment, accounted for.",
  "join.title": "Your circle, your turn.",
  "auth.aside.1": "Pay, get paid and hold eight currencies",
  "auth.aside.2": "Accept cards and mobile money for your community",
  "auth.aside.3": "A verifiable receipt for every payment",
  "landing.title": "Every payment, accounted for.",
  "landing.lede": "One account for your whole financial life: see what you can safely spend each day, pay with one card from any balance, accept cards and mobile money, save in circles and raise funds. For families, associations and businesses in Canada and across Africa.",
  "landing.nav.label": "Sections",
  "landing.nav.personal": "Personal",
  "landing.nav.business": "Business",
  "landing.nav.trust": "Trust",
  "landing.cta.open": "Open an account",
  "landing.cta.openFree": "Open a free account",
  "landing.cta.business": "Accept payments",
  "landing.hero.fine": "Free during the beta. Balances are test money until our licensed partners go live.",
  "landing.s.label": "An example CafonaPay account with recent payments",
  "landing.s.balance": "Total balance",
  "landing.s.receipt": "receipt #{n}",
  "landing.s.verified": "Receipt verified",
  "landing.s.1.who": "Grace Community Church",
  "landing.s.1.what": "Card payment received",
  "landing.s.2.who": "Maman Chantal, Douala",
  "landing.s.2.what": "Sent to Orange Money",
  "landing.s.3.who": "Car fund, turn 2 of 12",
  "landing.s.3.what": "Circle pot paid out",
  "landing.s.4.who": "Bafoussam well",
  "landing.s.4.what": "Fundraiser gift",
  "landing.rails": "Pay and get paid with",
  "landing.p.title": "Your whole financial life, in one place",
  "landing.p.lede": "Hold balances in eight currencies, move money between people, and run the shared money of your family or community from the same place.",
  "landing.p.send.title": "Send and request",
  "landing.p.send.body": "Pay anyone by @handle, email or phone number. Ask for money with a link they can pay by card, mobile money or their CafonaPay balance.",
  "landing.p.circles.title": "Save together in circles",
  "landing.p.circles.body": "Run a tontine, njangi or susu with set amounts and a fixed order of turns. Each pot is released automatically once every member has paid.",
  "landing.p.fund.title": "Raise funds in public",
  "landing.p.fund.body": "Open a fundraiser for school fees, a funeral or a village well. Live totals, anonymous giving, and a certificate listing every contribution.",
  "landing.p.fx.title": "Home and away, in eight currencies",
  "landing.p.fx.body": "Hold CAD, USD, EUR, XAF, XOF, NGN, GHS and KES side by side, so money for home and money for here stay clearly apart.",
  "landing.b.title": "Get paid by members, donors and customers",
  "landing.b.lede": "Associations, churches, mosques, schools and small businesses collect dues, donations and sales through one hosted checkout.",
  "landing.b.1": "Hosted checkout for Visa, Mastercard, American Express and mobile money",
  "landing.b.2": "Payment links and QR codes you can share on WhatsApp",
  "landing.b.3": "Team roles, a live dashboard, and payouts to members",
  "landing.b.4": "A REST API with test keys and signed webhooks",
  "landing.b.docs": "Read the API docs",
  "landing.b.mock": "Example checkout page for a community association",
  "landing.b.mock.org": "Association des Camerounais d'Ottawa",
  "landing.b.mock.item": "2026 membership",
  "landing.b.mock.card": "Card",
  "landing.b.mock.mobile": "Mobile money",
  "landing.b.mock.wallet": "CafonaPay",
  "landing.b.mock.pay": "Pay {amount}",
  "landing.b.mock.fine": "Card details are entered on our payment partner's secure page.",
  "landing.d.title": "Built for developers too",
  "landing.d.body": "Create a checkout session with one request, then receive a signed webhook when it's paid. Test keys work today.",
  "landing.d.code": "Example API request",
  "landing.t.title": "Verifiable by design",
  "landing.t.lede": "Every movement of money is written to a double-entry ledger and sealed with a receipt in a public hash chain. Anyone can check a receipt, and nobody can quietly change one.",
  "landing.r.from": "Car fund",
  "landing.t.ledger.title": "Double-entry ledger",
  "landing.t.ledger.body": "Every debit has a matching credit. Wallets can't go below zero, nobody can pay twice for the same turn, and each pot is released exactly once.",
  "landing.t.receipts.title": "Receipts anyone can verify",
  "landing.t.receipts.body": "Each receipt carries a SHA-256 fingerprint linked to the one before it. Scan its QR code to confirm it hasn't been altered.",
  "landing.t.security.title": "Your account, locked down",
  "landing.t.security.body": "Two-step sign-in with an authenticator app, a list of every device signed in, and sign-out everywhere in one tap.",
  "landing.t.partners.title": "Licensed payment partners",
  "landing.t.partners.body": "Cards are processed by Stripe and mobile money by CinetPay on their secure pages. During the beta, all money is test money.",
  "landing.t.verify": "Verify a receipt",
  "landing.final.title": "Open your account in two minutes.",
  "landing.final.body": "Use your email or your phone number. No fees during the beta.",
  "landing.footer.nav": "Footer",
  "landing.f.product": "Product",
  "landing.f.trust": "Trust",
  "landing.f.legal": "Legal",
  "home.morning": "Good morning, {name}",
  "home.afternoon": "Good afternoon, {name}",
  "home.evening": "Good evening, {name}",
  "home.balance": "Total balance",
  "home.actions": "Money actions",
  "home.requests": "Needs your attention",
  "home.req.title": "{name} asked you to pay",
  "home.due.title": "Your payment to {name} is due",
  "home.community": "Organizations and fundraisers",
  "home.raisedOf": "{raised} of {goal}",
  "home.promo.title": "Accept payments for your community",
  "home.promo.body": "Collect dues, donations and sales by card or mobile money, with a receipt for every payment.",
  "nav.plan": "Plan",
  "nav.card": "Card",
  "plan.title": "Money plan",
  "plan.lede": "Everything you have and everything that's due, in {base}.",
  "plan.addEntry": "Record an expense",
  "plan.addItem": "Add a bill or income",
  "plan.forecast.title": "Your balance over the next {days} days",
  "plan.fx": "Other currencies at indicative rates of {date}",
  "plan.forecast.aria": "Projected balance, day by day. Use the arrow keys to read each day.",
  "plan.power": "Financial power",
  "plan.safeToday": "Safe to spend today",
  "plan.status.safe": "On track",
  "plan.status.watch": "Keep an eye on it",
  "plan.status.at_risk": "At risk",
  "plan.reason.shortfall": "At this pace you run short on {date}. Move a payment, add money or cut spending before then.",
  "plan.reason.below_buffer": "Your balance dips below your safety cushion in the coming weeks.",
  "plan.reason.high_debt_service": "Rent and debt payments take {dsr} of your income; lenders treat more than 44% as too high.",
  "plan.reason.elevated_debt_service": "Rent and debt payments take {dsr} of your income; above 36% leaves little room.",
  "plan.reason.short_runway": "Your money would last less than a month without income.",
  "plan.reason.thin_runway": "Your savings cover less than three months of expenses.",
  "plan.reason.spending_exceeds_income": "You plan to spend more each month than you earn.",
  "plan.reason.overdue": "Something is overdue. Pay it or mark it as paid.",
  "plan.reason.no_income": "Add your income so the plan can tell how long your money lasts.",
  "plan.allGood": "Your bills are covered, your debts are in proportion and you have a cushion. Keep going.",
  "plan.stat.available": "Available to spend",
  "plan.stat.savings": "Savings (reserve)",
  "plan.stat.dueBeforePay": "Due before your next pay",
  "plan.stat.due30": "Due in the next 30 days",
  "plan.stat.nextPay": "Next pay",
  "plan.stat.runway": "Runway without income",
  "plan.stat.dsr": "Debt-service ratio",
  "plan.stat.netWorth": "Net worth",
  "plan.months": "{n} months",
  "plan.onboard.title": "Build your plan in three steps",
  "plan.onboard.1": "Add your bank, mobile money and credit card accounts",
  "plan.onboard.2": "Add your pay, with its next date",
  "plan.onboard.3": "Add rent, bills, loans and the money you send home",
  "plan.onboard.note": "Your CafonaPay balances and circle payments are already included.",
  "plan.markedDone": "Done, the plan moved it to the next date",
  "plan.upcoming": "Coming up",
  "plan.upcoming.empty": "Nothing due in the next 30 days.",
  "plan.today": "Today",
  "plan.kind.income": "Income",
  "plan.kind.bill": "Bill",
  "plan.kind.debt": "Debt repayment",
  "plan.kind.savings": "Savings",
  "plan.kind.remittance": "Money sent home",
  "plan.kind.circle": "Circle",
  "plan.overdue": "Overdue",
  "plan.markReceived": "Mark received",
  "plan.markPaid": "Mark paid",
  "plan.payNow": "Pay now",
  "plan.accounts": "Accounts",
  "plan.addAccount": "Add an account",
  "plan.acct.cafonapay": "CafonaPay balance",
  "plan.acct.chequing": "Chequing",
  "plan.acct.savings": "Savings",
  "plan.acct.mobile_money": "Mobile money",
  "plan.acct.cash": "Cash",
  "plan.acct.credit_card": "Credit card",
  "plan.acct.loan": "Loan",
  "plan.acct.investment": "Investment",
  "plan.acct.other": "Other asset",
  "plan.items": "Bills, income and commitments",
  "plan.items.empty": "Add your pay, rent and other regular payments to see what's coming.",
  "plan.freq.once": "Once",
  "plan.freq.weekly": "Every week",
  "plan.freq.biweekly": "Every two weeks",
  "plan.freq.monthly": "Every month",
  "plan.freq.quarterly": "Every three months",
  "plan.freq.yearly": "Every year",
  "plan.next": "next {date}",
  "plan.manageCircle": "Managed in the circle",
  "plan.remove": "Remove",
  "plan.spending": "Spending, last 30 days",
  "plan.spending.empty": "No spending recorded yet.",
  "plan.spending.note": "Everyday spending averages {daily} a day. The forecast uses this unless you set your own amount.",
  "plan.cat.housing": "Housing",
  "plan.cat.utilities": "Utilities",
  "plan.cat.food": "Food and groceries",
  "plan.cat.transport": "Transport",
  "plan.cat.health": "Health",
  "plan.cat.insurance": "Insurance",
  "plan.cat.education": "Education",
  "plan.cat.family": "Family",
  "plan.cat.remittance": "Money sent home",
  "plan.cat.debt": "Debt",
  "plan.cat.savings": "Savings",
  "plan.cat.income": "Income",
  "plan.cat.circle": "Circles",
  "plan.cat.giving": "Giving",
  "plan.cat.shopping": "Shopping",
  "plan.cat.leisure": "Leisure",
  "plan.cat.fees": "Fees",
  "plan.cat.other": "Other",
  "plan.recent": "Recent entries",
  "plan.recent.empty": "Expenses you record and CafonaPay payments appear here.",
  "plan.settings": "Plan settings",
  "plan.settings.buffer": "Safety cushion",
  "plan.settings.bufferHint": "The plan warns you before your balance would fall below this amount.",
  "plan.settings.daily": "Everyday spending per day",
  "plan.settings.dailyHint": "Leave empty to use your average from the last 30 days.",
  "plan.settings.horizon": "Forecast length",
  "plan.days": "{n} days",
  "plan.buffer": "Safety cushion",
  "plan.lowest": "Lowest {amount}, {date}",
  "plan.form.accountLede": "Enter the balance as it is today. You keep it current by recording expenses, or by editing it.",
  "plan.form.kind": "Type",
  "plan.form.name": "Name",
  "plan.form.namePh": "Chequing at Desjardins",
  "plan.form.institution": "Bank or provider (optional)",
  "plan.form.owed": "Amount owed today",
  "plan.form.balance": "Balance today",
  "plan.form.frequency": "How often",
  "plan.form.nextPay": "Next pay date",
  "plan.form.nextDue": "Next due date",
  "plan.form.category": "Category",
  "plan.form.paidInto": "Paid into",
  "plan.form.paidFrom": "Paid from",
  "plan.form.saveItem": "Add to plan",
  "plan.form.ph.income": "Salary",
  "plan.form.ph.bill": "Rent",
  "plan.form.ph.debt": "Car loan",
  "plan.form.ph.savings": "Emergency fund",
  "plan.form.ph.remittance": "Maman, Douala",
  "plan.form.needAccount": "Add the account the money came from first.",
  "plan.form.entryLede": "For money spent or received outside CafonaPay. CafonaPay payments are recorded automatically.",
  "plan.form.direction": "Money in or out",
  "plan.form.out": "Money out",
  "plan.form.in": "Money in",
  "plan.form.account": "Account",
  "plan.form.payee": "Where or who (optional)",
  "plan.form.date": "Date",
  "plan.form.saveEntry": "Record",
  "home.power.untilPay": "{due} is due before your next pay on {date}.",
  "home.power.setup": "Add your pay and bills to plan ahead.",
  "card.title": "Card",
  "card.intro.title": "One card for every balance",
  "card.intro.1": "Pays from the balance in the purchase's currency, or converts from your main one",
  "card.intro.2": "Bill guard keeps money for rent, bills and circle turns from being spent",
  "card.intro.3": "Freeze it, set limits, and switch off online, abroad or cash withdrawals in a tap",
  "card.intro.4": "Every purchase gets a receipt you can verify",
  "card.issue": "Get your virtual card",
  "card.sandboxNote": "Beta: this is a test card. It works with test purchases here, not in shops, until our card-issuing partner is live.",
  "card.aria": "CafonaPay Visa card ending in {last4}",
  "card.virtual": "Virtual",
  "card.holder": "Cardholder",
  "card.expires": "Expires",
  "card.frozen": "Frozen",
  "card.hide": "Hide details",
  "card.show": "Show card details",
  "card.unfreeze": "Unfreeze",
  "card.freeze": "Freeze card",
  "card.unfrozenToast": "Card unfrozen",
  "card.frozenToast": "Card frozen",
  "card.passwordToShow": "Enter your password to see the card number",
  "card.spentToday": "Spent today",
  "card.spentMonth": "Spent this month",
  "card.funding.title": "Paid from any balance",
  "card.funding.body": "A purchase uses the balance in its currency when it's enough, otherwise your {main} balance, converted at the indicative rate.",
  "card.controls": "Controls",
  "card.controls.saved": "Card updated",
  "card.billGuard": "Bill guard",
  "card.billGuardHint": "Declines purchases that would use money needed for bills and circle turns due in the next {days} days.",
  "card.online": "Online payments",
  "card.abroad": "Payments abroad",
  "card.atm": "Cash withdrawals",
  "card.limits": "Spending limits",
  "card.limit.per": "Per purchase",
  "card.limit.day": "Per day",
  "card.limit.month": "Per month",
  "card.limit.hint": "Leave a limit empty for no limit.",
  "card.limit.save": "Save limits",
  "card.approved": "{amount} at {merchant}: approved",
  "card.declinedToast": "Declined: {reason}",
  "card.decline.card_frozen": "card frozen",
  "card.decline.card_cancelled": "card cancelled",
  "card.decline.online_blocked": "online payments are off",
  "card.decline.atm_blocked": "cash withdrawals are off",
  "card.decline.abroad_blocked": "payments abroad are off",
  "card.decline.over_transaction_limit": "above the per-purchase limit",
  "card.decline.over_daily_limit": "above the daily limit",
  "card.decline.over_monthly_limit": "above the monthly limit",
  "card.decline.insufficient_funds": "not enough money",
  "card.decline.bill_guard": "bill guard: money set aside for upcoming bills",
  "card.test.title": "Make a test purchase",
  "card.test.body": "Test mode: the purchase goes through the same checks as a real one and is paid from your test balance.",
  "card.test.merchant": "Merchant",
  "card.test.abroad": "Pretend I'm abroad",
  "card.test.cta": "Pay with card",
  "card.channel.in_store": "In store",
  "card.channel.online": "Online",
  "card.channel.atm": "ATM",
  "card.tx": "Card activity",
  "card.tx.empty": "Purchases and declines will appear here.",
  "card.paidFrom": "paid from {cur}",
  "refund.done": "{amount} refunded",
  "refund.refunded": "{amount} refunded",
  "refund.amount": "Amount (up to {max})",
  "refund.reason": "Reason (optional)",
  "refund.cta": "Refund {amount}",
  "refund.ctaFull": "Refund {amount} in full",
  "refund.full": "Refunded",
  "refund.partial": "Partly refunded",
  "refund.open": "Refund",
  "errors.card_exists": "You already have a card.",
  "errors.issuing_unavailable": "Cards are available once our card-issuing partner is live.",
  "errors.refund_exceeds_payment": "That's more than is left to refund.",
  "errors.already_refunded": "This payment has already been refunded in full.",
  "errors.payment_not_refundable": "Only completed payments can be refunded.",
  "errors.sandbox_only": "Only available in test mode.",
  "errors.invalid_amount": "Enter a valid amount.",
  "errors.already_contributed": "You've already paid for this turn.",
  "errors.already_member": "You're already in this circle.",
  "errors.already_paid_out": "This turn's pot has already been paid out.",
  "errors.campaign_closed": "This fundraiser is closed.",
  "errors.circle_full": "This circle is already full.",
  "errors.circle_not_collecting": "This circle isn't collecting contributions right now.",
  "errors.circle_started": "This circle has already started.",
  "errors.immutable": "This record can't be changed.",
  "errors.not_awaiting_card": "This payment isn't waiting for card details.",
  "errors.not_awaiting_code": "This payment isn't waiting for a code.",
  "errors.not_enough_members": "Invite at least one more person before starting.",
  "errors.not_sandbox": "This payment isn't in test mode.",
  "errors.nothing_to_withdraw": "There's nothing to withdraw yet.",
  "errors.organizer_cannot_leave": "The organizer can't leave the circle.",
  "errors.payment_closed": "This payment link is no longer active.",
  "errors.payment_succeeded": "This payment is already complete.",
  "errors.request_not_open": "This request has already been paid or cancelled.",
  "errors.not_found": "We couldn't find this. The link may be wrong, or it was removed.",
  "errors.forbidden": "You don't have access to this.",
  "errors.payment_in_progress": "This payment is being completed in another window. Finish it there, or try again in 30 minutes.",
  "errors.members_changed": "Someone joined or left just now. Review the members and start again.",
  "errors.account_locked": "Too many wrong attempts on this account. Wait 15 minutes and try again.",
  "errors.too_many_lookups": "Too many searches. Wait a few minutes and try again.",
  "errors.idempotency_key_reused": "This request was already sent with different details. Try again.",
  "errors.request_in_progress": "Your previous attempt is still being processed. Try again in a moment.",
  "errors.kind_locked": "This account has entries. Create a new account for a card or loan instead.",
  "errors.invalid_date": "Enter a real date.",
  "errors.duplicate_member": "Each member can appear only once.",
  "errors.card_declined": "The card was declined by the bank. Try again or choose another method.",
  "errors.operator_declined": "The operator declined the payment. Check your mobile money balance.",
  "errors.too_big": "That's too large.",
  "errors.too_small": "That's too small.",
  "errors.date_in_past": "Pick a date in the future.",
  "errors.https_required": "Use an address that starts with https://",
  "errors.invalid_format": "Check the format.",
  "errors.invalid_value": "Choose one of the options.",
  "errors.card_over_daily_limit": "Above the card's daily limit.",
  "errors.amount_too_big": "That's more than one payment can carry (10 million maximum).",
  "errors.no_decimals": "This currency has no cents. Enter a whole amount.",
  "audit.card.issued": "Card issued",
  "audit.card.revealed": "Card details viewed",
  "audit.card.frozen": "Card frozen",
  "audit.card.unfrozen": "Card unfrozen",
  "audit.card.cancelled": "Card cancelled",
  "audit.card.controls_changed": "Card controls changed",
  "landing.p.plan.title": "Know what you can spend today",
  "landing.p.plan.body": "Bring your bank, mobile money and credit cards into one plan. CafonaPay forecasts every bill and payday and warns you weeks before you'd run short.",
  "landing.p.card.title": "One card for every balance",
  "landing.p.card.body": "A Visa that pays from the right currency, with limits, instant freeze, and a bill guard that won't let a purchase eat your rent.",
  "subnav.plan": "Plan",
  "subnav.receipts": "Receipts",
  "subnav.insights": "Insights",
  "scope.label": "Whose spending",
  "scope.me": "Me",
  "rcpt.title": "Receipts",
  "rcpt.lede": "Every purchase, itemized: e-receipts arrive by e-mail, paper ones by photo.",
  "rcpt.scan": "Scan a receipt",
  "rcpt.scanHint": "Take a photo, or choose a photo or PDF",
  "rcpt.reading": "Reading your receipt…",
  "rcpt.paste": "Paste receipt text",
  "rcpt.pasteHint": "From an e-mail or a website",
  "rcpt.pasteLabel": "Receipt text",
  "rcpt.itemize": "Itemize",
  "rcpt.email.title": "Forward your e-receipts",
  "rcpt.email.body": "Give this private address to stores that offer e-receipts, or have your mailbox forward receipts to it. Each one is itemized automatically.",
  "rcpt.email.bodyOrg": "Team members forward expense receipts to this address. Each one is itemized into the organization's spending.",
  "rcpt.email.moneyOnly": "Only the organization's owners, admins and treasurers can see this address. You can still scan or paste receipts.",
  "rcpt.email.how1": "At checkout, ask for an e-receipt and give this address (Metro, Shoppers, Walmart, IKEA and many others offer it).",
  "rcpt.email.how2": "In Gmail or Outlook, create a filter that forwards e-mails with \"receipt\" or \"reçu\" in the subject to this address.",
  "rcpt.email.how3": "Keep the address private: anything sent to it lands in your receipts. You can replace it at any time.",
  "rcpt.email.test": "Test mode: e-mail delivery is switched on in production. Try it with a sample e-receipt.",
  "rcpt.email.sample": "Send a sample e-receipt",
  "rcpt.rotate": "Replace this address",
  "rcpt.rotated": "New address ready. The old one no longer works.",
  "rcpt.list": "Your receipts",
  "rcpt.toInsights": "See insights",
  "rcpt.empty": "No receipts yet. Scan one, paste one, or forward an e-receipt.",
  "rcpt.unknownMerchant": "Receipt",
  "rcpt.items": "{n} items",
  "rcpt.needsReview": "needs review",
  "rcpt.matchedCard": "paid by card",
  "rcpt.matchedPlan": "in your plan",
  "rcpt.status.ready": "Itemized",
  "rcpt.status.needs_review": "Needs review",
  "rcpt.status.processing": "Reading",
  "rcpt.status.failed": "Couldn't read",
  "rcpt.reviewHint": "We couldn't read every line of this receipt. Check the items and the total, or scan a sharper photo.",
  "rcpt.col.item": "Item",
  "rcpt.col.category": "Category",
  "rcpt.col.qty": "Qty",
  "rcpt.col.total": "Amount",
  "rcpt.subtotal": "Subtotal",
  "rcpt.tax": "Taxes",
  "rcpt.total": "Total",
  "rcpt.source.email": "Received by e-mail",
  "rcpt.source.scan": "Scanned",
  "rcpt.source.text": "Pasted",
  "rcpt.source.manual": "Entered by hand",
  "rcpt.readByAi": "read by AI",
  "rcpt.readByRules": "read automatically",
  "rcpt.notRead": "not read yet",
  "rcpt.imageAlt": "Photo of the receipt",
  "rcpt.payment": "Payment",
  "rcpt.matchedCardLong": "Matched to your CafonaPay card purchase.",
  "rcpt.matchedPlanLong": "Recorded as an expense in your money plan.",
  "rcpt.orgNote": "Organization receipts count in the organization's insights.",
  "rcpt.noAccounts": "Paid outside CafonaPay? Add the account you paid from to record it.",
  "rcpt.addExpenseHint": "Paid outside CafonaPay? Record it on the account you paid from.",
  "rcpt.addExpense": "Record as an expense",
  "rcpt.expenseAdded": "Recorded in your money plan",
  "rcpt.delete": "Delete receipt",
  "ins.title": "Insights",
  "ins.lede": "What you buy, what it costs and how prices move, from your itemized receipts.",
  "ins.range": "Period",
  "ins.range.week": "Week",
  "ins.range.month": "Month",
  "ins.range.year": "Year",
  "ins.range.decade": "Decade",
  "ins.prev": "Previous period",
  "ins.next": "Next period",
  "ins.spent": "Spent on itemized purchases",
  "ins.vsPrev.week": "vs last week",
  "ins.vsPrev.month": "vs last month",
  "ins.vsPrev.year": "vs last year",
  "ins.vsPrev.decade": "vs last decade",
  "ins.counts": "{receipts} receipts · {items} items",
  "ins.chartAria": "Spending per period. Select a bar to read its total.",
  "ins.drillHint": "Select a bar to open that period.",
  "ins.empty": "No itemized receipts in this period yet.",
  "ins.addReceipts": "Add receipts",
  "ins.products": "What you bought",
  "ins.col.product": "Product",
  "ins.col.qty": "Qty",
  "ins.col.avg": "Avg. price",
  "ins.col.total": "Spent",
  "ins.times": "bought {n} times",
  "ins.categories": "By category",
  "ins.merchants": "By store",
  "ins.visits": "{n} visits",
  "ins.p.total": "Spent, all time",
  "ins.p.purchases": "Times bought",
  "ins.p.price": "Price paid over time",
  "ins.p.priceAria": "Unit price of {product} at each purchase",
  "ins.p.history": "Every purchase",
  "ins.p.where": "Where it's cheapest",
  "ins.p.cheapest": "Cheapest",
  "ins.p.byYear": "By year",
  "errors.unsupported": "Use a photo (JPEG, PNG, WebP, HEIC) or a PDF.",
  "errors.too_large": "The file is larger than 10 MB.",
  "errors.already_matched": "This receipt is already linked to a payment.",
  "icat.produce": "Fruit and vegetables",
  "icat.dairy_eggs": "Dairy and eggs",
  "icat.meat_fish": "Meat and fish",
  "icat.bakery": "Bakery",
  "icat.pantry": "Pantry",
  "icat.frozen": "Frozen",
  "icat.beverages": "Drinks",
  "icat.alcohol": "Alcohol",
  "icat.snacks": "Snacks",
  "icat.household": "Household",
  "icat.personal_care": "Personal care",
  "icat.baby": "Baby",
  "icat.pets": "Pets",
  "icat.health": "Health and pharmacy",
  "icat.clothing": "Clothing",
  "icat.electronics": "Electronics",
  "icat.home": "Home",
  "icat.fuel": "Fuel",
  "icat.restaurant": "Restaurants",
  "icat.services": "Services",
  "icat.other": "Other",
} as const;

export type MessageKey = keyof typeof en;

const fr: Record<MessageKey, string> = {
  "beta.banner": "Bêta : argent fictif. Aucun vrai fonds ne circule.",

  "nav.home": "Accueil",
  "nav.circles": "Cercles",
  "nav.pay": "Payer",
  "nav.campaigns": "Collectes",
  "nav.me": "Moi",
  "nav.activity": "Activité",
  "nav.signIn": "Se connecter",
  "nav.getStarted": "Commencer",
  "nav.openApp": "Ouvrir l'appli",

  "join.lede":
    "Gère ta tontine sur CafonaPay. Chacun cotise, un membre reçoit toute la cagnotte, puis le tour passe, suivi au centime près et visible par tout le cercle.",
  "landing.footer": "CafonaPay est une plateforme de paiement et de gestion financière, en bêta. Les soldes sont fictifs jusqu'à l'arrivée de nos partenaires de paiement agréés.",

  "auth.signIn": "Se connecter",
  "auth.signUp": "Créer votre compte",
  "auth.email": "E-mail",
  "auth.contact": "E-mail ou numéro de téléphone",
  "auth.contactHint": "Votre e-mail ou votre numéro de mobile. Pour un numéro d'un autre pays, commencez par + et l'indicatif.",
  "auth.country": "Pays",
  "errors.generic": "Un problème est survenu. Réessayez.",
  "errors.invalid_request": "Vérifiez les champs signalés.",
  "errors.invalid_credentials": "Cet e-mail ou numéro et ce mot de passe ne correspondent pas.",
  "errors.contact_required": "Entrez votre e-mail ou votre numéro de téléphone.",
  "errors.invalid_email": "Entrez une adresse e-mail valide.",
  "errors.invalid_phone": "Entrez un numéro valide. Pour un autre pays, commencez par + et l'indicatif.",
  "errors.email_taken": "Un compte utilise déjà cet e-mail. Connectez-vous plutôt.",
  "errors.phone_taken": "Un compte utilise déjà ce numéro. Connectez-vous plutôt.",
  "errors.handle_taken": "Ce @pseudo est déjà pris. Essayez-en un autre.",
  "errors.invalid_handle": "De 3 à 20 lettres, chiffres ou tirets bas.",
  "errors.password_too_short": "Au moins 10 caractères.",
  "errors.too_short": "C'est trop court.",
  "errors.required": "Ce champ est obligatoire.",
  "errors.too_many_attempts": "Trop de tentatives. Patientez 15 minutes puis réessayez.",
  "errors.offline": "Vous êtes hors ligne. Vérifiez votre connexion et réessayez.",
  "errors.insufficient_funds": "Votre solde est insuffisant. Ajoutez d'abord de l'argent.",
  "errors.not_found_user": "Aucun utilisateur CafonaPay avec ce @pseudo, cet e-mail ou ce numéro.",
  "errors.internal": "Un problème est survenu de notre côté. Réessayez dans un instant.",
  "auth.password": "Mot de passe",
  "auth.name": "Nom complet",
  "auth.handle": "Choisissez un @pseudo",
  "auth.handleHint": "Vos proches l'utilisent pour vous envoyer de l'argent.",
  "auth.passwordHint": "Au moins 10 caractères.",
  "auth.currency": "Devise principale",
  "auth.noAccount": "Nouveau sur CafonaPay ?",
  "auth.haveAccount": "Vous avez déjà un compte ?",
  "auth.signOut": "Se déconnecter",

  "home.add": "Ajouter",
  "home.send": "Envoyer",
  "home.request": "Demander",
  "home.withdraw": "Retirer",
  "home.circles": "Vos cercles",
  "home.circles.empty": "Vous n'êtes dans aucun cercle. Créez-en un avec des personnes de confiance, ou rejoignez-en un avec un code.",
  "home.recent": "Activité récente",
  "home.activity.empty": "Rien pour l'instant. Ajoutez de l'argent pour commencer.",
  "home.seeAll": "Tout voir",

  "circles.title": "Cercles",
  "circles.new": "Créer un cercle",
  "circles.join": "Rejoindre avec un code",
  "circles.code": "Code d'invitation",
  "circles.empty": "Aucun cercle pour l'instant.",
  "circles.turnOf": "Tour {n} sur {total}",
  "circles.potFor": "Cagnotte pour {name}",
  "circles.yourTurn": "C'est votre tour",
  "circles.forming": "Membres en cours",
  "circles.active": "En cours",
  "circles.completed": "Terminé",
  "circles.cancelled": "Annulé",
  "circles.youPaid": "Vous avez payé ce tour",
  "circles.youOwe": "Votre cotisation est due",
  "circles.members": "{n} membres sur {total}",

  "circle.name": "Nom du cercle",
  "circle.namePlaceholder": "Fonds voiture, njangi familial…",
  "circle.description": "C'est pour quoi ? (facultatif)",
  "circle.amount": "Chaque membre paie",
  "circle.frequency": "Fréquence",
  "circle.weekly": "Chaque semaine",
  "circle.biweekly": "Toutes les deux semaines",
  "circle.monthly": "Chaque mois",
  "circle.size": "Nombre de membres",
  "circle.start": "Date du premier paiement",
  "circle.order": "Ordre des tours",
  "circle.order.join_order": "Dans l'ordre d'arrivée",
  "circle.order.random": "Tirage au sort au départ",
  "circle.order.manual": "Je le fixe moi-même",
  "circle.summary": "À chaque tour, la cagnotte est de {pot}. Le cercle compte {n} tours et se termine le {end}.",
  "circle.create": "Créer le cercle",
  "circle.invite": "Inviter des membres",
  "circle.inviteBody": "Partagez ce lien ou ce code. On peut rejoindre jusqu'à ce que le cercle soit complet.",
  "circle.copy": "Copier le lien",
  "circle.copied": "Lien copié",
  "circle.share": "Partager",
  "circle.startNow": "Lancer le cercle",
  "circle.startHint": "Le lancement fige les membres et l'ordre des tours.",
  "circle.pay": "Payer {amount} pour ce tour",
  "circle.paid": "Payé",
  "circle.waiting": "Pas encore",
  "circle.received": "Reçu",
  "circle.schedule": "Calendrier",
  "circle.thisTurn": "Ce tour",
  "circle.due": "Échéance {date}",
  "circle.collected": "{collected} sur {pot} collectés",
  "circle.payoutDone": "Cagnotte versée à {name}",
  "circle.moveUp": "Monter",
  "circle.moveDown": "Descendre",
  "circle.saveOrder": "Enregistrer l'ordre",
  "circle.leave": "Quitter le cercle",
  "circle.cancel": "Annuler le cercle",
  "circle.organizer": "Organisateur",
  "circle.you": "vous",
  "circle.completedNote": "Chaque membre a eu son tour. Ce cercle est terminé.",
  "circle.joinTitle": "{name} vous invite dans un cercle",
  "circle.joinCta": "Rejoindre ce cercle",
  "circle.joinDetails": "{amount} {frequency} · {joined} sur {total} inscrits",
  "circle.startsOn": "Premier paiement le {date}",

  "pay.title": "Payer",
  "pay.send": "Envoyer de l'argent",
  "pay.request": "Demander de l'argent",
  "pay.to": "À",
  "pay.toPlaceholder": "@pseudo, e-mail ou téléphone",
  "pay.amount": "Montant",
  "pay.currency": "Devise",
  "pay.note": "Note (facultatif)",
  "pay.notePlaceholder": "C'est pour quoi ?",
  "pay.review": "Vérifier",
  "pay.confirmSend": "Envoyer {amount}",
  "pay.sent": "{amount} envoyé à {name}",
  "pay.from": "Demander à une personne précise (facultatif)",
  "pay.fromHint": "Laissez vide et toute personne ayant le lien pourra payer.",
  "pay.createRequest": "Créer le lien de paiement",
  "pay.requestReady": "Votre lien de paiement est prêt",
  "pay.requestsSent": "Vos liens de paiement",
  "pay.requestsEmpty": "Les liens que vous créez pour vous faire payer apparaîtront ici, avec qui a payé.",
  "pay.payRequest": "Payer {amount}",
  "pay.requestFrom": "{name} vous demande",
  "pay.requestPaid": "Cette demande a été payée.",
  "pay.requestCancelled": "Cette demande a été annulée.",
  "pay.cancelRequest": "Annuler le lien",
  "pay.open": "Ouverte",
  "pay.paidStatus": "Payée",
  "pay.cancelledStatus": "Annulée",

  "wallet.add": "Ajouter de l'argent",
  "wallet.addBody": "Mode test : l'argent est ajouté instantanément et n'est pas réel.",
  "wallet.withdrawBody": "Mode test : le retrait est simulé et aucun argent réel ne bouge. En mode réel, l'argent va au compte bancaire ou au numéro mobile money enregistré dans votre profil.",
  "wallet.method": "Payer avec",
  "wallet.method.interac": "Virement Interac",
  "wallet.method.card": "Carte de débit ou de crédit",
  "wallet.method.mobile_money": "Mobile money",
  "wallet.method.bank": "Virement bancaire",
  "wallet.addCta": "Ajouter {amount}",
  "wallet.added": "{amount} ajouté à votre portefeuille",
  "wallet.withdraw": "Retirer",
  "wallet.withdrawCta": "Retirer {amount}",
  "wallet.withdrawn": "{amount} en route",
  "wallet.available": "{amount} disponible",

  "campaigns.title": "Collectes",
  "campaigns.new": "Lancer une collecte",
  "campaigns.empty": "Les collectes que vous lancez ou soutenez apparaîtront ici.",
  "campaign.title": "Titre",
  "campaign.story": "Racontez l'histoire",
  "campaign.storyHint": "Pourquoi collectez-vous, et à quoi servira l'argent ?",
  "campaign.goal": "Objectif",
  "campaign.endsOn": "Date de fin (facultatif)",
  "campaign.create": "Publier la collecte",
  "campaign.raised": "{raised} collectés sur {goal}",
  "campaign.backers": "{n} soutiens",
  "campaign.give": "Donner",
  "campaign.giveCta": "Donner {amount}",
  "campaign.message": "Message (facultatif)",
  "campaign.anonymous": "Masquer mon nom",
  "campaign.thanks": "Merci. Vos {amount} sont arrivés.",
  "campaign.withdraw": "Verser {amount} sur mon portefeuille",
  "campaign.close": "Clôturer la collecte",
  "campaign.closed": "Cette collecte est clôturée.",
  "campaign.by": "Organisée par {name}",
  "campaign.recent": "Soutiens récents",
  "campaign.signInToGive": "Se connecter pour donner",
  "campaign.share": "Partager la collecte",

  "me.title": "Votre compte",
  "me.language": "Langue",
  "me.currency": "Devise principale",
  "me.save": "Enregistrer",
  "me.saved": "Enregistré",
  "me.install": "Installer l'appli",
  "me.installBody": "Ajoutez CafonaPay à votre écran d'accueil pour y accéder en un geste.",
  "me.about": "À propos de cette bêta",

  "nav.community": "Communauté",
  "common.copy": "Copier",
  "wallet.addBodyCheckout": "Vous confirmerez le paiement à l'écran suivant. Votre portefeuille est crédité dès que le paiement passe.",
  "wallet.withdrawTo": "Envoyer vers",
  "co.yourWallet": "Votre portefeuille CafonaPay",
  "co.topUpSub": "Ajout d'argent",
  "co.verified": "Vérifié",
  "co.testBanner": "Mode test : aucun argent réel ne circule. Les cartes et codes de test sont indiqués à chaque étape.",
  "co.secured": "Paiement sécurisé · connexion chiffrée",
  "co.chooseMethod": "Choisissez comment payer",
  "co.card": "Carte",
  "co.mobileMoney": "Mobile money",
  "co.wallet": "Solde CafonaPay",
  "co.walletSignIn": "Connectez-vous pour payer avec votre solde",
  "co.mmUnavailable": "Indisponible en {currency}",
  "co.operator": "Opérateur",
  "co.mmNumber": "Numéro mobile money",
  "co.pay": "Payer {amount}",
  "co.tryAgain": "Réessayez ou choisissez un autre moyen.",
  "co.backToMerchant": "Retourner au site",
  "co.otherMethod": "Autres moyens de paiement",
  "co.cardName": "Nom sur la carte",
  "co.cardNumber": "Numéro de carte",
  "co.expiry": "Expiration (MM/AA)",
  "co.testCards": "Cartes de test",
  "co.approveTitle": "Validez sur votre téléphone",
  "co.approveBody": "Le {phone} va recevoir une demande de votre opérateur. Validez-la, puis saisissez le code reçu.",
  "co.code": "Code de confirmation",
  "co.testCode": "Code de test : {code}",
  "co.confirm": "Confirmer le paiement",
  "co.confirming": "Confirmation de votre paiement…",
  "co.confirmingBody": "Cela prend généralement quelques secondes. Vous pouvez garder cette page ouverte.",
  "co.success": "Paiement effectué",
  "co.paidWith": "Payé avec",
  "co.date": "Date",
  "co.reference": "Référence",
  "co.toWallet": "Aller à mon portefeuille",
  "co.expired": "Ce lien de paiement a expiré",
  "co.canceled": "Ce paiement a été annulé",
  "co.closedBody": "Rien n'a été débité. Demandez un nouveau lien si vous souhaitez encore payer.",
  "pstatus.requires_payment_method": "En attente",
  "pstatus.requires_action": "En cours",
  "pstatus.processing": "Traitement",
  "pstatus.succeeded": "Payé",
  "pstatus.failed": "Échoué",
  "pstatus.canceled": "Annulé",
  "pstatus.expired": "Expiré",
  "org.title": "Vos organisations",
  "org.new": "Créer une organisation",
  "org.newBody": "Une organisation collecte de l'argent pour un groupe : association, église, caisse familiale, tontine ou petite entreprise. Elle a son propre solde, des membres avec des rôles et des liens de paiement.",
  "org.empty": "Collectez cotisations, offrandes et contributions avec des liens de paiement, par carte et mobile money.",
  "org.name": "Nom de l'organisation",
  "org.namePlaceholder": "Association des Camerounais d'Ottawa",
  "org.kind": "Type",
  "org.create": "Créer l'organisation",
  "org.kind.association": "Association",
  "org.kind.church": "Église",
  "org.kind.mosque": "Mosquée",
  "org.kind.tontine": "Groupe de tontine",
  "org.kind.family": "Caisse familiale",
  "org.kind.business": "Entreprise",
  "org.kind.school": "École",
  "org.kind.other": "Autre",
  "org.role.owner": "Propriétaire",
  "org.role.admin": "Admin",
  "org.role.treasurer": "Trésorier",
  "org.role.member": "Membre",
  "org.roleHelp.member": "Les membres voient l'organisation et son activité.",
  "org.roleHelp.treasurer": "Les trésoriers créent des liens de paiement et versent des fonds aux membres.",
  "org.roleHelp.admin": "Les admins gèrent aussi les membres, les clés API et les webhooks.",
  "org.unverified": "Pas encore vérifiée",
  "org.balance": "Solde de l'organisation",
  "org.tab.overview": "Aperçu",
  "org.tab.members": "Membres",
  "org.tab.developers": "Développeurs",
  "org.received": "Reçu (net)",
  "org.payments": "Paiements",
  "org.fees": "Frais",
  "org.fee": "frais",
  "org.getPaid": "Se faire payer",
  "org.getPaidBody": "Créez un lien que chacun peut payer par carte, mobile money ou solde CafonaPay. Partagez-le sur WhatsApp, par e-mail ou sur une affiche.",
  "org.linkFor": "C'est pour quoi ?",
  "org.linkForPlaceholder": "Cotisation annuelle, offrande du dimanche…",
  "org.createLink": "Créer le lien de paiement",
  "org.linkReady": "Votre lien de paiement est prêt",
  "org.anotherLink": "Créer un autre lien",
  "org.payout": "Verser à un membre",
  "org.payoutBody": "Transférez de l'argent de l'organisation vers le portefeuille CafonaPay d'un membre. Chaque versement est enregistré avec la personne qui l'a approuvé.",
  "org.payoutTo": "Membre",
  "org.payoutToHint": "Laissez vide pour vous verser les fonds.",
  "org.payoutCta": "Verser",
  "org.recent": "Paiements",
  "org.noPayments": "Les paiements reçus par cette organisation apparaîtront ici.",
  "org.addMember": "Ajouter un membre",
  "org.memberAdded": "Membre ajouté",
  "dev.keys": "Clés API",
  "dev.keysBody": "Utilisez une clé secrète sur votre serveur pour créer des sessions de paiement. Ne la mettez jamais dans un site ou une appli.",
  "dev.keyLabel": "Nom de la clé",
  "dev.keyLabelPlaceholder": "Site web",
  "dev.createKey": "Créer la clé",
  "dev.keyOnce": "Copiez votre clé secrète maintenant. Pour votre sécurité, elle ne sera plus affichée.",
  "dev.secretOnce": "Copiez votre secret de signature maintenant. Il ne sera plus affiché.",
  "dev.lastUsed": "utilisée le {date}",
  "dev.neverUsed": "jamais utilisée",
  "dev.revoke": "Révoquer",
  "dev.revoked": "Révoquée",
  "dev.webhooks": "Webhooks",
  "dev.webhooksBody": "CafonaPay prévient votre serveur quand un paiement réussit ou échoue, avec une signature pour vérifier que l'appel vient bien de nous.",
  "dev.endpoint": "URL de destination",
  "dev.addEndpoint": "Ajouter",
  "dev.sendTest": "Envoyer un événement de test",
  "dev.quickstart": "Démarrage rapide",
  "dev.quickstartBody": "Créez une session de paiement depuis votre serveur, puis envoyez le payeur vers l'url renvoyée.",
  "dev.signatureNote": "Les webhooks portent un en-tête CafonaPay-Signature (HMAC-SHA256). Vérifiez-le avant de faire confiance à un événement.",
  "dev.docs": "Documentation complète de l'API",
  "notif.title": "Notifications",
  "notif.empty": "Vous êtes à jour. Rappels et alertes de paiement apparaîtront ici.",
  "notif.unread": "{n} notifications non lues",
  "circle.late": "En retard",
  "circle.lateNote": "Ce tour a dépassé son échéance. Les membres ont {days} jour(s) de grâce avant que le paiement soit considéré en défaut.",
  "circle.overdueNote": "Ce tour est en défaut : {n} membres sur {total} ont payé. La cagnotte est bloquée jusqu'à ce que tout le monde paie.",
  "circle.remind": "Relancer les membres en attente",
  "circle.reminded": "Rappel envoyé à {n} membre(s)",
  "errors.invalid_card": "Vérifiez le numéro de carte.",
  "errors.invalid_expiry": "Utilisez le format MM/AA.",
  "errors.invalid_cvc": "Saisissez les 3 ou 4 chiffres au dos de la carte.",
  "errors.test_card_required": "Mode test : utilisez une des cartes de test ci-dessous.",
  "errors.card_expired": "Cette carte a expiré.",
  "errors.invalid_otp": "Ce code n'est pas bon. Vérifiez le message de votre opérateur.",
  "errors.invalid_network": "Choisissez votre opérateur mobile money.",
  "errors.not_member": "Les fonds ne peuvent être versés qu'aux membres de l'organisation.",
  "common.cancel": "Annuler",
  "common.back": "Retour",
  "confirm.yes": "Confirmer",
  "seo.home.title": "CafonaPay · Paiement et gestion financière des communautés",
  "seo.home.description": "Planifiez votre argent, suivez vos reçus article par article, gérez tontines et collectes, acceptez cartes et mobile money. Au Canada et en Afrique.",
  "seo.terms.title": "Conditions d'utilisation · CafonaPay",
  "seo.terms.description": "Les conditions d'utilisation de CafonaPay, plateforme de paiement et de gestion financière, pendant la bêta.",
  "seo.privacy.title": "Politique de confidentialité · CafonaPay",
  "seo.privacy.description": "Les renseignements personnels que CafonaPay recueille, pourquoi, et comment vous les contrôlez (LPRPDE et Loi 25).",
  "seo.dev.title": "API de paiement pour les développeurs · CafonaPay",
  "seo.dev.description": "Acceptez cartes et mobile money avec une seule API : sessions de paiement, paiements, remboursements et webhooks signés, en dollars canadiens et en francs CFA.",
  "seo.verify.title": "Vérifier un reçu CafonaPay",
  "seo.verify.description": "Vérifiez qu'un reçu CafonaPay est authentique : chaque reçu est scellé dans une chaîne d'empreintes publique que chacun peut vérifier.",
  "subnav.finances": "Mes finances",
  "subnav.proof": "Preuve de fonds",
  "fin.title": "Mes finances",
  "fin.lede": "Vos comptes bancaires au même endroit, en lecture seule. Vous vous connectez sur l'écran de votre institution ou de l'agrégateur : CafonaPay ne voit ni ne conserve jamais votre mot de passe bancaire.",
  "fin.noAdvice": "CafonaPay organise vos informations. Il ne donne pas de conseils de placement personnalisés.",
  "fin.net": "Valeur nette",
  "fin.assets": "Ce que vous avez",
  "fin.debts": "Ce que vous devez",
  "fin.banks": "Comptes liés",
  "fin.connect": "Lier un compte",
  "fin.simulated": "Mode test : ces institutions sont simulées et affichent des données de démonstration. En production, vous vous connectez via Flinks ou Plaid.",
  "fin.pick": "Choisissez votre institution",
  "fin.connected": "Lié : {accounts} comptes et {transactions} opérations importés.",
  "fin.synced": "À jour.",
  "fin.disconnected": "Déconnecté. Ces comptes ont quitté votre plan.",
  "fin.empty": "Aucun compte lié pour l'instant. Liez-en un pour voir vos dépenses et vos soldes se mettre à jour d'eux-mêmes.",
  "fin.privacy": "Accès en lecture seule. Vous pouvez vous déconnecter à tout moment.",
  "fin.readOnly": "Lecture seule",
  "fin.lastSync": "mis à jour le {date}",
  "fin.demo": "données de démonstration",
  "fin.sync": "Mettre à jour",
  "fin.disconnect": "Déconnecter",
  "fin.disconnectPrompt": "Déconnecter {name} ? Ses comptes quittent votre plan ; vous pourrez le lier de nouveau.",
  "fin.budgets": "Budgets du mois",
  "fin.budgets.empty": "Aucune dépense ce mois-ci pour l'instant.",
  "fin.limit": "Plafond mensuel",
  "fin.setLimit": "Fixer un plafond",
  "fin.editLimit": "Modifier le plafond",
  "fin.addBudget": "Ajouter un budget pour…",
  "fin.goals": "Objectifs",
  "fin.goals.empty": "Fixez un objectif : fonds d'urgence, frais de scolarité, voyage au pays.",
  "fin.addGoal": "Ajouter un objectif",
  "fin.goal.name": "Objectif",
  "fin.goal.namePh": "Fonds d'urgence",
  "fin.goal.target": "Montant visé",
  "fin.goal.date": "Pour le (facultatif)",
  "fin.goal.account": "Suivi avec",
  "fin.goal.manual": "Je le mettrai à jour moi-même",
  "fin.goal.by": "pour le {date}",
  "fin.goal.deletePrompt": "Supprimer l'objectif {name} ?",
  "pof.title": "Preuve de fonds",
  "pof.lede": "Organisez vos relevés et l'historique de vos soldes pour un dossier d'Immigration Canada, et exportez un résumé.",
  "pof.notCertified": "Non certifié. Ce dossier organise vos propres documents ; il ne certifie rien. Immigration Canada demande une lettre officielle de chaque institution financière.",
  "pof.period": "Période",
  "pof.months": "{n} mois",
  "pof.export": "Exporter le résumé (PDF)",
  "pof.summary": "Résumé",
  "pof.total": "Disponible aujourd'hui",
  "pof.average": "Moyenne sur {n} mois",
  "pof.accounts": "Soldes à chaque fin de mois",
  "pof.account": "Compte",
  "pof.totalRow": "Total ({currency})",
  "pof.monthEnds": "Total à chaque fin de mois",
  "pof.noAccounts": "Ajoutez vos comptes bancaires dans le plan financier ou liez-les dans Mes finances.",
  "pof.testMoney": "Les soldes CafonaPay sont de l'argent fictif pendant la bêta et sont exclus de ce dossier.",
  "pof.documents": "Documents",
  "pof.noDocs": "Aucun document pour l'instant. Ajoutez vos relevés et les lettres de vos institutions.",
  "pof.kind": "Type",
  "pof.kind.bank_statement": "Relevé bancaire",
  "pof.kind.investment_statement": "Relevé de placement",
  "pof.kind.gift_letter": "Lettre de don",
  "pof.kind.property": "Document immobilier",
  "pof.kind.other": "Autre",
  "pof.docTitle": "Titre",
  "pof.docTitlePh": "Lettre Desjardins, mai 2027",
  "pof.institution": "Institution (facultatif)",
  "pof.from": "Du",
  "pof.to": "Au",
  "pof.file": "Fichier (PDF ou photo, 10 Mo max.)",
  "pof.add": "Ajouter le document",
  "pof.added": "Document ajouté.",
  "pof.deletePrompt": "Supprimer {title} ?",
  "pof.irccNote": "Vérifiez les montants et les documents qu'Immigration Canada exige pour votre programme.",
  "pof.irccLink": "IRCC : preuve de fonds",
  "pof.pdf.badge": "NON CERTIFIÉ — DOCUMENT ORGANISÉ PAR LE TITULAIRE",
  "pof.pdf.badgeSub": "Ce résumé n'est pas un document officiel et n'est certifié ni par CafonaPay ni par une institution financière.",
  "pof.pdf.title": "Dossier preuve de fonds : résumé organisé",
  "pof.pdf.generated": "généré le {date}",
  "pof.pdf.period": "Période : {from} à {to}",
  "pof.pdf.ircc": "Immigration Canada exige une lettre officielle de chaque institution financière. Joignez ces lettres à votre demande.",
  "pof.pdf.footer": "CafonaPay · plateforme de paiement et de gestion financière · Non certifié : organisé à partir des informations saisies ou liées par le titulaire.",
  "common.copied": "Copié",
  "circle.mode": "Comment les membres paient",
  "circle.mode.coordination": "Les membres se paient entre eux",
  "circle.mode.coordination.body": "À chaque tour, les membres envoient leur part directement au bénéficiaire par virement Interac. CafonaPay gère le calendrier, les rappels et les confirmations, sans jamais détenir l'argent.",
  "circle.mode.wallet": "Cagnotte dans CafonaPay (argent fictif)",
  "circle.mode.wallet.body": "Les membres cotisent dans une cagnotte versée automatiquement. Argent fictif seulement, jusqu'à ce qu'un partenaire agréé détienne les fonds.",
  "circle.thisTurnChip": "Ce tour",
  "circle.confirmed": "Confirmé",
  "circle.declared": "Déclaré",
  "circle.reliability": "fiabilité {score} %",
  "circle.reliability.help": "À temps : {onTime} · en retard : {late} · manqués : {missed}",
  "circle.declaredToast": "Déclaré. {name} confirmera à la réception.",
  "circle.confirmedToast": "Paiement confirmé.",
  "circle.disputedToast": "Marqué comme non reçu. Le membre a été prévenu.",
  "circle.contactSaved": "Enregistré. Les membres le verront à votre tour.",
  "circle.contact": "Où vous recevez votre tour",
  "circle.contactHint": "Votre courriel ou téléphone Interac. Seuls les membres du cercle le voient.",
  "circle.coord.yourTurn": "Votre tour : {amount}",
  "circle.coord.yourTurnBody": "{n} membres vous envoient chacun {amount} directement. Confirmez chaque paiement à son arrivée sur votre compte.",
  "circle.coord.addContact": "Indiquez où recevoir l'argent pour que les membres sachent où l'envoyer.",
  "circle.coord.says": "{name} dit avoir envoyé {amount}",
  "circle.ref": "réf. {ref}",
  "circle.coord.received": "Je l'ai reçu",
  "circle.coord.notReceived": "Pas reçu",
  "circle.coord.confirm": "Confirmer",
  "circle.coord.dispute": "Marquer non reçu",
  "circle.coord.confirmPrompt": "Confirmer la réception de {amount} de {name} ? C'est irréversible.",
  "circle.coord.disputePrompt": "Signaler à {name} que l'argent n'est pas arrivé ? Le membre pourra le déclarer de nouveau.",
  "circle.coord.send": "Envoyez {amount} à {name}",
  "circle.coord.to": "Interac à",
  "circle.coord.noContact": "{name} n'a pas encore indiqué où recevoir l'argent. Demandez-le dans le groupe.",
  "circle.coord.due": "À payer le {date}",
  "circle.coord.confirmedMine": "{name} a confirmé votre paiement.",
  "circle.coord.waitingConfirm": "Déclaré. En attente de la confirmation de {name}.",
  "circle.coord.disputedMine": "{name} n'a pas reçu votre paiement. Vérifiez le virement, puis déclarez-le de nouveau.",
  "circle.coord.how": "Comment vous avez payé",
  "circle.coord.reference": "Référence (facultatif)",
  "circle.coord.referenceHint": "Le numéro de référence Interac aide le membre à le retrouver.",
  "circle.coord.iSent": "J'ai envoyé {amount}",
  "circle.coord.myContact": "Où vous recevez votre propre tour",
  "circle.method.interac": "Virement Interac",
  "circle.method.mobile_money": "Mobile money",
  "circle.method.cash": "Espèces",
  "circle.method.other": "Autre",
  "adm.tab.overview": "Vue d'ensemble",
  "adm.tab.releases": "Versions",
  "adm.tab.docs": "Documentation",
  "adm.tab.features": "Fonctionnalités et conformité",
  "adm.tab.bank": "Voie bancaire",
  "adm.tab.roadmap": "Feuille de route",
  "adm.env.staging": "Préproduction",
  "adm.env.production": "Production",
  "adm.rel.lede": "Chaque déploiement réussi dans cet environnement ({env}), enregistré par le workflow de déploiement.",
  "adm.rel.empty": "Aucun déploiement enregistré pour l'instant. Le prochain apparaîtra ici.",
  "adm.rel.pr": "Pull request",
  "adm.rel.run": "Exécution CI",
  "adm.rel.ciOk": "CI réussie",
  "adm.docs.search": "Rechercher dans la documentation",
  "adm.docs.none": "Aucune page ne correspond.",
  "adm.lvl.N0_sans_agrement": "N0 · Sans agrément",
  "adm.lvl.N1_partenaire": "N1 · Partenaire agréé",
  "adm.lvl.N2_agrement_propre": "N2 · Agréments propres",
  "adm.lvl.N3_banque": "N3 · Charte bancaire",
  "adm.st.idea": "Idée",
  "adm.st.dev": "En dev",
  "adm.st.beta": "Bêta argent fictif",
  "adm.st.production": "Production",
  "adm.cost.itemized": "{amount} ventilés ; le reste est l'enveloppe de la phase",
  "adm.cost.estimated": "Total estimé",
  "adm.cost.phase": "Inclus dans le budget de la phase",
  "adm.filters": "Filtres",
  "adm.f.level": "Niveau",
  "adm.f.regulator": "Régulateur",
  "adm.f.status": "Statut",
  "adm.f.all": "Tous",
  "adm.f.count": "{shown} sur {total} fonctionnalités · {allowed} autorisées en production avec de l'argent réel",
  "adm.guard.help": "Garde-fou : les fonctionnalités de niveau agréé exigent une autorisation enregistrée avant que l'argent réel circule.",
  "adm.guard.ok": "Autorisée en production",
  "adm.guard.locked": "Argent fictif seulement",
  "adm.cost": "Coût estimé",
  "adm.delay": "Délai estimé",
  "adm.months": "{n} mois",
  "adm.monthsRange": "{min} à {max} mois",
  "adm.regulators": "Régulateurs",
  "adm.verified": "Dernière vérification",
  "adm.more": "Prérequis, autorisation, sources",
  "adm.prereq": "Prérequis",
  "adm.auth": "Autorisation :",
  "adm.auth.none": "Non requise (CafonaPay ne détient aucun fonds client).",
  "adm.auth.missing": "Aucune. L'argent réel reste désactivé tant qu'une référence de partenaire ou un numéro d'agrément n'est pas inscrit au registre.",
  "adm.sources": "Sources",
  "adm.bank.lede": "Options à long terme si CafonaPay devait un jour recevoir des dépôts. Chaque option est à valider par un avocat ; les montants sont des estimations.",
  "adm.bank.capital": "Capital et investissement",
  "adm.bank.steps": "Étapes",
  "adm.bank.lawyer": "À valider par un avocat.",
  "adm.bank.realism.faible": "Peu réaliste",
  "adm.bank.realism.moyen": "Envisageable",
  "adm.bank.realism.eleve": "Le plus réaliste",
  "adm.road.window": "Mois {from} à {to}",
  "adm.road.milestones": "Jalons",
  "adm.road.funding": "Financement",
  "adm.road.scenarios": "Scénarios de pré-amorçage (400 000 $)",
  "adm.fund.add": "Ajouter une source",
  "adm.fund.deleted": "Source de financement retirée.",
  "adm.fund.deletePrompt": "Retirer cette source de financement ?",
  "adm.fund.upTo": "Jusqu'à {amount}",
  "adm.fund.nameFr": "Nom (français)",
  "adm.fund.nameEn": "Nom (anglais)",
  "adm.fund.type": "Type",
  "adm.fund.max": "Maximum (CAD, facultatif)",
  "adm.fund.notes": "Notes",
  "adm.fund.url": "Lien",
  "adm.fund.type.pret": "Prêt",
  "adm.fund.type.capital": "Capital",
  "adm.fund.type.subvention": "Subvention",
  "adm.fund.type.credit_impot": "Crédit d'impôt",
  "adm.fund.type.accompagnement": "Accompagnement",
  "adm.fund.type.autre": "Autre",
  "plan.edit": "Modifier",
  "plan.editAccount": "Modifier le compte",
  "plan.editItem": "Modifier la facture ou le revenu",
  "plan.form.save": "Enregistrer",
  "plan.removeAccount": "Retirer ce compte du plan",
  "plan.removeAccountPrompt": "Retirer ce compte ? Ses écritures restent dans l'historique, mais il ne compte plus dans votre plan.",
  "plan.removeItemPrompt": "Retirer {name} de votre plan ?",
  "plan.deleteEntry": "Supprimer",
  "plan.deleteEntryPrompt": "Supprimer cette écriture ? Le solde du compte est rétabli.",
  "plan.entryDeleted": "Écriture supprimée. Le solde a été rétabli.",
  "plan.status.setup": "À configurer",
  "plan.setupNote": "Ajoutez vos comptes, votre paie et vos factures pour voir ce que vous pouvez dépenser chaque jour.",
  "co.failFunds": "Le solde de la carte est insuffisant.",
  "confirm.send": "Envoyer {amount} à {name} ? L'argent envoyé ne peut pas être récupéré.",
  "confirm.revoke": "Révoquer cette clé ? Tout ce qui l'utilise cesse de fonctionner immédiatement.",
  "confirm.refund": "Rembourser {amount} au payeur ? C'est irréversible.",
  "confirm.pay": "Payer {amount} à {name} ?",
  "confirm.withdraw": "Retirer {amount} ?",
  "common.continue": "Continuer",
  "common.done": "Terminé",
  "common.loading": "Chargement…",
  "common.retry": "Réessayer",
  "common.offline": "Vous êtes hors ligne. Les soldes se mettront à jour à la reconnexion.",
  "common.update": "Une nouvelle version de CafonaPay est prête.",
  "common.reload": "Mettre à jour",
  "common.notFound": "Cette page n'existe pas.",
  "common.goHome": "Retour à l'accueil",
  "landing.developers": "Développeurs",
  "legal.terms": "Conditions d'utilisation",
  "legal.privacy": "Politique de confidentialité",
  "legal.consent.before": "En créant un compte, vous acceptez les",
  "legal.consent.and": "et la",
  "verify.title": "Vérifier un reçu",
  "verify.summary": "Contrôler un reçu ou un certificat CafonaPay",
  "verify.lede": "Chaque paiement sur CafonaPay reçoit un reçu scellé dans une chaîne de hachage publique. Saisissez un numéro de reçu, ou scannez son code QR, pour vérifier qu'il n'a pas été modifié.",
  "verify.idLabel": "Numéro ou lien du reçu",
  "verify.cta": "Vérifier",
  "verify.kicker": "Reçu de paiement CafonaPay",
  "verify.valid": "Vérifié",
  "verify.invalid": "Non conforme",
  "verify.number": "Reçu",
  "verify.id": "Identifiant du reçu",
  "verify.pending": "Scellement…",
  "verify.hash": "Empreinte (SHA-256)",
  "verify.prevHash": "Empreinte du reçu précédent",
  "verify.receipt": "Reçu",
  "verify.check.sealed": "Scellé dans la chaîne publique des reçus",
  "verify.check.hash": "Détails inchangés depuis l'émission",
  "verify.check.chain": "Relié au reçu précédent : rien n'a été inséré ni supprimé",
  "verify.check.ledger": "Conforme à l'écriture comptable, toujours équilibrée à zéro",
  "cert.print": "Imprimer ou enregistrer en PDF",
  "cert.short": "Certificat",
  "cert.campaignLink": "Certificat de collecte",
  "cert.kicker.campaign": "Certificat de collecte · cagnotte",
  "cert.kicker.circle": "Certificat de collecte · cercle",
  "cert.receipts": "{n} reçus",
  "cert.totalIn": "Collecté",
  "cert.totalOut": "Versé",
  "cert.held": "Encore détenu",
  "cert.empty": "Aucun mouvement d'argent pour l'instant.",
  "cert.from": "De",
  "cert.to": "À",
  "cert.counterparty": "Qui",
  "cert.amount": "Montant",
  "cert.date": "Date",
  "cert.subject.circle": "Cercle",
  "cert.subject.campaign": "Cagnotte",
  "cert.subject.org": "Organisation",
  "cert.digest": "Empreinte du certificat (SHA-256 de tous les reçus)",
  "cert.head": "Tête de chaîne à l'émission (reçu n° {n})",
  "cert.generated": "Émis le",
  "cert.qr": "Code QR menant à ce certificat",
  "cert.scan": "Scannez pour vérifier en ligne",
  "cert.disclaimer": "Les reçus sont scellés dans une chaîne de hachage SHA-256 : toute modification ultérieure est détectable. C'est une preuve d'intégrité, pas un acte notarié. Bêta : les montants sont fictifs.",
  "security.title": "Sécurité",
  "security.summary": "Connexion en deux étapes, appareils, mot de passe",
  "security.code": "Code à 6 chiffres",
  "security.codeHint": "Depuis votre application d'authentification (Google Authenticator, Microsoft Authenticator, 1Password…).",
  "security.2fa.title": "Connexion en deux étapes",
  "security.2fa.body": "Demandez un code d'une application d'authentification à chaque connexion : un mot de passe volé ne suffira plus pour entrer dans votre compte.",
  "security.2fa.start": "Activer la connexion en deux étapes",
  "security.2fa.passwordHint": "Confirmez votre identité avant d'associer une application d'authentification.",
  "security.2fa.step1": "Scannez ce code QR avec votre application d'authentification, ou saisissez la clé.",
  "security.2fa.step2": "Saisissez le code à 6 chiffres qu'elle affiche.",
  "security.2fa.qr": "Code QR pour ajouter CafonaPay à votre application d'authentification",
  "security.2fa.openApp": "Ouvrir dans votre application d'authentification",
  "security.2fa.manual": "Ou saisissez cette clé :",
  "security.2fa.confirm": "Confirmer et activer",
  "security.2fa.on": "Connexion en deux étapes activée",
  "security.2fa.off": "Connexion en deux étapes désactivée",
  "security.2fa.enabled": "Connexion en deux étapes activée",
  "security.2fa.enabledBody": "Un code de votre application d'authentification vous sera demandé à chaque connexion.",
  "security.2fa.disable": "Désactiver la connexion en deux étapes",
  "security.devices.title": "Où vous êtes connecté",
  "security.devices.this": "Cet appareil",
  "security.devices.since": "Connecté le {date}",
  "security.devices.seen": "actif le {date}",
  "security.devices.signOut": "Déconnecter",
  "security.devices.signOutOthers": "Déconnecter tous les autres appareils",
  "security.devices.signedOut": "{n} autre(s) appareil(s) déconnecté(s)",
  "security.password.title": "Mot de passe",
  "security.password.body": "Changer votre mot de passe vous déconnecte de tous vos autres appareils.",
  "security.password.current": "Mot de passe actuel",
  "security.password.new": "Nouveau mot de passe",
  "security.password.cta": "Changer le mot de passe",
  "security.password.changed": "Mot de passe changé",
  "security.log.title": "Activité de sécurité",
  "security.log.empty": "Rien pour l'instant.",
  "audit.auth.login": "Connexion",
  "audit.auth.login_failed": "Tentative de connexion échouée",
  "audit.auth.signup": "Compte créé",
  "audit.auth.2fa_enabled": "Connexion en deux étapes activée",
  "audit.auth.2fa_disabled": "Connexion en deux étapes désactivée",
  "audit.auth.password_changed": "Mot de passe changé",
  "audit.auth.session_revoked": "Appareil déconnecté",
  "audit.auth.sessions_revoked_others": "Tous les autres appareils déconnectés",
  "admin.title": "Console d'administration",
  "admin.summary": "Rapprochement, activité, journal d'audit",
  "admin.reconcile": "Rapprochement des comptes",
  "admin.balanced": "Comptes équilibrés",
  "admin.unbalanced": "À vérifier",
  "admin.recheck": "Vérifier à nouveau",
  "admin.check.zero": "Partie double : chaque devise totalise zéro",
  "admin.check.drift": "Les soldes correspondent aux écritures",
  "admin.check.entries": "Chaque écriture comptable est équilibrée",
  "admin.check.negative": "Aucun portefeuille de membre sous zéro",
  "admin.check.chain": "Chaîne des reçus intacte",
  "admin.chainCount": "{sealed} scellés, {pending} en attente",
  "admin.users": "Membres",
  "admin.orgs": "Organisations",
  "admin.entries": "Écritures comptables",
  "admin.volume": "Volume par devise",
  "admin.payments": "Paiements en ligne",
  "admin.audit": "Journal d'audit",
  "errors.totp_required": "Saisissez le code à 6 chiffres de votre application d'authentification.",
  "errors.invalid_code": "Ce code n'a pas fonctionné. Les codes changent toutes les 30 secondes.",
  "errors.invalid_password": "Votre mot de passe actuel n'est pas correct.",
  "errors.totp_already_enabled": "La connexion en deux étapes est déjà activée.",
  "errors.totp_not_setup": "Commencez d'abord la configuration.",
  "brand.tagline": "Chaque paiement, en toute traçabilité.",
  "join.title": "Ton cercle, ton tour.",
  "auth.aside.1": "Payez, soyez payé et détenez huit devises",
  "auth.aside.2": "Acceptez cartes et mobile money pour votre communauté",
  "auth.aside.3": "Un reçu vérifiable pour chaque paiement",
  "landing.title": "Chaque paiement, en toute traçabilité.",
  "landing.lede": "Un seul compte pour toute votre vie financière : voyez chaque jour ce que vous pouvez dépenser sans risque, payez avec une carte depuis n'importe quel solde, acceptez cartes et mobile money, épargnez en cercles et collectez des fonds. Pour les familles, associations et entreprises au Canada et en Afrique.",
  "landing.nav.label": "Sections",
  "landing.nav.personal": "Particuliers",
  "landing.nav.business": "Organisations",
  "landing.nav.trust": "Confiance",
  "landing.cta.open": "Ouvrir un compte",
  "landing.cta.openFree": "Ouvrir un compte gratuit",
  "landing.cta.business": "Accepter des paiements",
  "landing.hero.fine": "Gratuit pendant la bêta. Les soldes sont fictifs jusqu'à l'activation de nos partenaires agréés.",
  "landing.s.label": "Exemple de compte CafonaPay avec des paiements récents",
  "landing.s.balance": "Solde total",
  "landing.s.receipt": "reçu n° {n}",
  "landing.s.verified": "Reçu vérifié",
  "landing.s.1.who": "Église Grace Community",
  "landing.s.1.what": "Paiement par carte reçu",
  "landing.s.2.who": "Maman Chantal, Douala",
  "landing.s.2.what": "Envoyé sur Orange Money",
  "landing.s.3.who": "Fonds voiture, tour 2 sur 12",
  "landing.s.3.what": "Cagnotte du cercle versée",
  "landing.s.4.who": "Puits de Bafoussam",
  "landing.s.4.what": "Don à une collecte",
  "landing.rails": "Payez et soyez payé avec",
  "landing.p.title": "Toute votre vie financière, au même endroit",
  "landing.p.lede": "Détenez des soldes en huit devises, envoyez de l'argent à vos proches et gérez l'argent commun de votre famille ou de votre communauté au même endroit.",
  "landing.p.send.title": "Envoyer et demander",
  "landing.p.send.body": "Payez n'importe qui par @identifiant, courriel ou numéro de téléphone. Demandez de l'argent avec un lien payable par carte, mobile money ou solde CafonaPay.",
  "landing.p.circles.title": "Épargner ensemble en cercles",
  "landing.p.circles.body": "Gérez une tontine, un njangi ou un susu avec des montants fixes et un ordre de tours défini. Chaque cagnotte est versée automatiquement dès que tous les membres ont payé.",
  "landing.p.fund.title": "Collecter des fonds en public",
  "landing.p.fund.body": "Ouvrez une collecte pour des frais de scolarité, des funérailles ou le puits du village. Totaux en direct, dons anonymes et certificat listant chaque contribution.",
  "landing.p.fx.title": "Ici et au pays, en huit devises",
  "landing.p.fx.body": "Détenez CAD, USD, EUR, XAF, XOF, NGN, GHS et KES côte à côte : l'argent pour le pays et l'argent d'ici restent bien séparés.",
  "landing.b.title": "Faites-vous payer par vos membres, donateurs et clients",
  "landing.b.lede": "Associations, églises, mosquées, écoles et petites entreprises encaissent cotisations, dons et ventes avec un seul paiement en ligne.",
  "landing.b.1": "Page de paiement sécurisée pour Visa, Mastercard, American Express et mobile money",
  "landing.b.2": "Liens de paiement et codes QR à partager sur WhatsApp",
  "landing.b.3": "Rôles d'équipe, tableau de bord en direct et versements aux membres",
  "landing.b.4": "Une API REST avec clés de test et webhooks signés",
  "landing.b.docs": "Lire la documentation de l'API",
  "landing.b.mock": "Exemple de page de paiement pour une association",
  "landing.b.mock.org": "Association des Camerounais d'Ottawa",
  "landing.b.mock.item": "Cotisation 2026",
  "landing.b.mock.card": "Carte",
  "landing.b.mock.mobile": "Mobile money",
  "landing.b.mock.wallet": "CafonaPay",
  "landing.b.mock.pay": "Payer {amount}",
  "landing.b.mock.fine": "Les données de carte sont saisies sur la page sécurisée de notre partenaire de paiement.",
  "landing.d.title": "Pensé aussi pour les développeurs",
  "landing.d.body": "Créez une session de paiement en une requête, puis recevez un webhook signé quand elle est payée. Les clés de test fonctionnent dès aujourd'hui.",
  "landing.d.code": "Exemple de requête API",
  "landing.t.title": "Vérifiable par conception",
  "landing.t.lede": "Chaque mouvement d'argent est inscrit dans un grand livre en partie double et scellé par un reçu dans une chaîne de hachage publique. Tout le monde peut vérifier un reçu, et personne ne peut le modifier en douce.",
  "landing.r.from": "Fonds voiture",
  "landing.t.ledger.title": "Comptabilité en partie double",
  "landing.t.ledger.body": "Chaque débit a son crédit. Aucun portefeuille ne passe sous zéro, personne ne paie deux fois le même tour, et chaque cagnotte est versée une seule fois.",
  "landing.t.receipts.title": "Des reçus vérifiables par tous",
  "landing.t.receipts.body": "Chaque reçu porte une empreinte SHA-256 reliée au précédent. Scannez son code QR pour confirmer qu'il n'a pas été modifié.",
  "landing.t.security.title": "Votre compte, bien protégé",
  "landing.t.security.body": "Connexion en deux étapes avec une application d'authentification, liste de tous vos appareils connectés et déconnexion à distance en un geste.",
  "landing.t.partners.title": "Partenaires de paiement agréés",
  "landing.t.partners.body": "Les cartes sont traitées par Stripe et le mobile money par CinetPay, sur leurs pages sécurisées. Pendant la bêta, tout l'argent est fictif.",
  "landing.t.verify": "Vérifier un reçu",
  "landing.final.title": "Ouvrez votre compte en deux minutes.",
  "landing.final.body": "Avec votre courriel ou votre numéro de téléphone. Aucuns frais pendant la bêta.",
  "landing.footer.nav": "Pied de page",
  "landing.f.product": "Produit",
  "landing.f.trust": "Confiance",
  "landing.f.legal": "Mentions légales",
  "home.morning": "Bonjour, {name}",
  "home.afternoon": "Bon après-midi, {name}",
  "home.evening": "Bonsoir, {name}",
  "home.balance": "Solde total",
  "home.actions": "Actions",
  "home.requests": "À traiter",
  "home.req.title": "{name} vous demande un paiement",
  "home.due.title": "Votre cotisation à {name} est due",
  "home.community": "Organisations et collectes",
  "home.raisedOf": "{raised} sur {goal}",
  "home.promo.title": "Acceptez des paiements pour votre communauté",
  "home.promo.body": "Encaissez cotisations, dons et ventes par carte ou mobile money, avec un reçu pour chaque paiement.",
  "nav.plan": "Plan",
  "nav.card": "Carte",
  "plan.title": "Plan financier",
  "plan.lede": "Tout ce que vous avez et tout ce qui est dû, en {base}.",
  "plan.addEntry": "Noter une dépense",
  "plan.addItem": "Ajouter une facture ou un revenu",
  "plan.forecast.title": "Votre solde sur les {days} prochains jours",
  "plan.fx": "Autres devises au taux indicatif du {date}",
  "plan.forecast.aria": "Solde prévu, jour par jour. Utilisez les flèches pour lire chaque jour.",
  "plan.power": "Capacité financière",
  "plan.safeToday": "Dépense possible aujourd'hui",
  "plan.status.safe": "Sur la bonne voie",
  "plan.status.watch": "À surveiller",
  "plan.status.at_risk": "À risque",
  "plan.reason.shortfall": "À ce rythme, il vous manquera de l'argent le {date}. Décalez un paiement, ajoutez de l'argent ou réduisez vos dépenses d'ici là.",
  "plan.reason.below_buffer": "Votre solde passe sous votre coussin de sécurité dans les semaines à venir.",
  "plan.reason.high_debt_service": "Loyer et remboursements prennent {dsr} de vos revenus ; les prêteurs jugent trop élevé au-delà de 44 %.",
  "plan.reason.elevated_debt_service": "Loyer et remboursements prennent {dsr} de vos revenus ; au-delà de 36 %, la marge est faible.",
  "plan.reason.short_runway": "Sans revenu, votre argent durerait moins d'un mois.",
  "plan.reason.thin_runway": "Votre épargne couvre moins de trois mois de dépenses.",
  "plan.reason.spending_exceeds_income": "Vous prévoyez de dépenser plus chaque mois que vous ne gagnez.",
  "plan.reason.overdue": "Un paiement est en retard. Payez-le ou marquez-le comme payé.",
  "plan.reason.no_income": "Ajoutez vos revenus pour savoir combien de temps votre argent durera.",
  "plan.allGood": "Vos factures sont couvertes, vos dettes sont raisonnables et vous avez un coussin. Continuez ainsi.",
  "plan.stat.available": "Disponible à dépenser",
  "plan.stat.savings": "Épargne (réserve)",
  "plan.stat.dueBeforePay": "Dû avant votre prochaine paie",
  "plan.stat.due30": "Dû dans les 30 prochains jours",
  "plan.stat.nextPay": "Prochaine paie",
  "plan.stat.runway": "Autonomie sans revenu",
  "plan.stat.dsr": "Ratio d'endettement",
  "plan.stat.netWorth": "Valeur nette",
  "plan.months": "{n} mois",
  "plan.onboard.title": "Construisez votre plan en trois étapes",
  "plan.onboard.1": "Ajoutez vos comptes bancaires, mobile money et cartes de crédit",
  "plan.onboard.2": "Ajoutez votre paie, avec sa prochaine date",
  "plan.onboard.3": "Ajoutez loyer, factures, prêts et l'argent envoyé au pays",
  "plan.onboard.note": "Vos soldes CafonaPay et vos cotisations de cercle sont déjà inclus.",
  "plan.markedDone": "C'est fait, le plan l'a reporté à la prochaine date",
  "plan.upcoming": "À venir",
  "plan.upcoming.empty": "Rien à payer dans les 30 prochains jours.",
  "plan.today": "Aujourd'hui",
  "plan.kind.income": "Revenu",
  "plan.kind.bill": "Facture",
  "plan.kind.debt": "Remboursement",
  "plan.kind.savings": "Épargne",
  "plan.kind.remittance": "Envoi au pays",
  "plan.kind.circle": "Cercle",
  "plan.overdue": "En retard",
  "plan.markReceived": "Marquer reçu",
  "plan.markPaid": "Marquer payé",
  "plan.payNow": "Payer",
  "plan.accounts": "Comptes",
  "plan.addAccount": "Ajouter un compte",
  "plan.acct.cafonapay": "Solde CafonaPay",
  "plan.acct.chequing": "Compte courant",
  "plan.acct.savings": "Épargne",
  "plan.acct.mobile_money": "Mobile money",
  "plan.acct.cash": "Espèces",
  "plan.acct.credit_card": "Carte de crédit",
  "plan.acct.loan": "Prêt",
  "plan.acct.investment": "Placement",
  "plan.acct.other": "Autre actif",
  "plan.items": "Factures, revenus et engagements",
  "plan.items.empty": "Ajoutez votre paie, votre loyer et vos paiements réguliers pour voir ce qui arrive.",
  "plan.freq.once": "Une fois",
  "plan.freq.weekly": "Chaque semaine",
  "plan.freq.biweekly": "Toutes les deux semaines",
  "plan.freq.monthly": "Chaque mois",
  "plan.freq.quarterly": "Tous les trois mois",
  "plan.freq.yearly": "Chaque année",
  "plan.next": "prochain le {date}",
  "plan.manageCircle": "Géré dans le cercle",
  "plan.remove": "Retirer",
  "plan.spending": "Dépenses, 30 derniers jours",
  "plan.spending.empty": "Aucune dépense enregistrée pour l'instant.",
  "plan.spending.note": "Vos dépenses courantes sont en moyenne de {daily} par jour. La prévision utilise ce montant, sauf si vous fixez le vôtre.",
  "plan.cat.housing": "Logement",
  "plan.cat.utilities": "Services publics",
  "plan.cat.food": "Alimentation",
  "plan.cat.transport": "Transport",
  "plan.cat.health": "Santé",
  "plan.cat.insurance": "Assurance",
  "plan.cat.education": "Éducation",
  "plan.cat.family": "Famille",
  "plan.cat.remittance": "Envois au pays",
  "plan.cat.debt": "Dettes",
  "plan.cat.savings": "Épargne",
  "plan.cat.income": "Revenus",
  "plan.cat.circle": "Cercles",
  "plan.cat.giving": "Dons",
  "plan.cat.shopping": "Achats",
  "plan.cat.leisure": "Loisirs",
  "plan.cat.fees": "Frais",
  "plan.cat.other": "Autre",
  "plan.recent": "Écritures récentes",
  "plan.recent.empty": "Les dépenses que vous notez et les paiements CafonaPay apparaissent ici.",
  "plan.settings": "Réglages du plan",
  "plan.settings.buffer": "Coussin de sécurité",
  "plan.settings.bufferHint": "Le plan vous prévient avant que votre solde ne passe sous ce montant.",
  "plan.settings.daily": "Dépenses courantes par jour",
  "plan.settings.dailyHint": "Laissez vide pour utiliser votre moyenne des 30 derniers jours.",
  "plan.settings.horizon": "Durée de la prévision",
  "plan.days": "{n} jours",
  "plan.buffer": "Coussin de sécurité",
  "plan.lowest": "Au plus bas {amount}, le {date}",
  "plan.form.accountLede": "Indiquez le solde d'aujourd'hui. Vous le tenez à jour en notant vos dépenses, ou en le modifiant.",
  "plan.form.kind": "Type",
  "plan.form.name": "Nom",
  "plan.form.namePh": "Compte courant Desjardins",
  "plan.form.institution": "Banque ou fournisseur (facultatif)",
  "plan.form.owed": "Montant dû aujourd'hui",
  "plan.form.balance": "Solde aujourd'hui",
  "plan.form.frequency": "Fréquence",
  "plan.form.nextPay": "Date de la prochaine paie",
  "plan.form.nextDue": "Prochaine échéance",
  "plan.form.category": "Catégorie",
  "plan.form.paidInto": "Versé sur",
  "plan.form.paidFrom": "Payé depuis",
  "plan.form.saveItem": "Ajouter au plan",
  "plan.form.ph.income": "Salaire",
  "plan.form.ph.bill": "Loyer",
  "plan.form.ph.debt": "Prêt auto",
  "plan.form.ph.savings": "Fonds d'urgence",
  "plan.form.ph.remittance": "Maman, Douala",
  "plan.form.needAccount": "Ajoutez d'abord le compte d'où vient l'argent.",
  "plan.form.entryLede": "Pour l'argent dépensé ou reçu hors de CafonaPay. Les paiements CafonaPay sont enregistrés automatiquement.",
  "plan.form.direction": "Entrée ou sortie",
  "plan.form.out": "Sortie",
  "plan.form.in": "Entrée",
  "plan.form.account": "Compte",
  "plan.form.payee": "Où ou qui (facultatif)",
  "plan.form.date": "Date",
  "plan.form.saveEntry": "Enregistrer",
  "home.power.untilPay": "{due} est dû avant votre prochaine paie, le {date}.",
  "home.power.setup": "Ajoutez votre paie et vos factures pour voir venir.",
  "card.title": "Carte",
  "card.intro.title": "Une carte pour tous vos soldes",
  "card.intro.1": "Paie depuis le solde dans la devise de l'achat, ou convertit depuis votre devise principale",
  "card.intro.2": "La protection des factures empêche de dépenser l'argent du loyer, des factures et des cercles",
  "card.intro.3": "Gelez-la, fixez des plafonds, coupez les paiements en ligne, à l'étranger ou les retraits en un geste",
  "card.intro.4": "Chaque achat a un reçu vérifiable",
  "card.issue": "Obtenir ma carte virtuelle",
  "card.sandboxNote": "Bêta : c'est une carte de test. Elle fonctionne avec des achats de test ici, pas en magasin, jusqu'à l'activation de notre partenaire émetteur.",
  "card.aria": "Carte Visa CafonaPay se terminant par {last4}",
  "card.virtual": "Virtuelle",
  "card.holder": "Titulaire",
  "card.expires": "Expire",
  "card.frozen": "Gelée",
  "card.hide": "Masquer",
  "card.show": "Afficher les détails",
  "card.unfreeze": "Dégeler",
  "card.freeze": "Geler la carte",
  "card.unfrozenToast": "Carte dégelée",
  "card.frozenToast": "Carte gelée",
  "card.passwordToShow": "Saisissez votre mot de passe pour voir le numéro",
  "card.spentToday": "Dépensé aujourd'hui",
  "card.spentMonth": "Dépensé ce mois-ci",
  "card.funding.title": "Payée depuis n'importe quel solde",
  "card.funding.body": "Un achat utilise le solde dans sa devise s'il suffit, sinon votre solde en {main}, converti au taux indicatif.",
  "card.controls": "Contrôles",
  "card.controls.saved": "Carte mise à jour",
  "card.billGuard": "Protection des factures",
  "card.billGuardHint": "Refuse les achats qui utiliseraient l'argent des factures et cotisations dues dans les {days} prochains jours.",
  "card.online": "Paiements en ligne",
  "card.abroad": "Paiements à l'étranger",
  "card.atm": "Retraits d'espèces",
  "card.limits": "Plafonds de dépenses",
  "card.limit.per": "Par achat",
  "card.limit.day": "Par jour",
  "card.limit.month": "Par mois",
  "card.limit.hint": "Laissez vide pour aucun plafond.",
  "card.limit.save": "Enregistrer les plafonds",
  "card.approved": "{amount} chez {merchant} : accepté",
  "card.declinedToast": "Refusé : {reason}",
  "card.decline.card_frozen": "carte gelée",
  "card.decline.card_cancelled": "carte annulée",
  "card.decline.online_blocked": "paiements en ligne désactivés",
  "card.decline.atm_blocked": "retraits désactivés",
  "card.decline.abroad_blocked": "paiements à l'étranger désactivés",
  "card.decline.over_transaction_limit": "au-dessus du plafond par achat",
  "card.decline.over_daily_limit": "au-dessus du plafond journalier",
  "card.decline.over_monthly_limit": "au-dessus du plafond mensuel",
  "card.decline.insufficient_funds": "solde insuffisant",
  "card.decline.bill_guard": "protection des factures : argent réservé aux prochaines factures",
  "card.test.title": "Faire un achat de test",
  "card.test.body": "Mode test : l'achat passe par les mêmes contrôles qu'un vrai et est payé avec votre solde fictif.",
  "card.test.merchant": "Commerçant",
  "card.test.abroad": "Simuler un achat à l'étranger",
  "card.test.cta": "Payer par carte",
  "card.channel.in_store": "En magasin",
  "card.channel.online": "En ligne",
  "card.channel.atm": "Guichet",
  "card.tx": "Activité de la carte",
  "card.tx.empty": "Les achats et refus apparaîtront ici.",
  "card.paidFrom": "payé en {cur}",
  "refund.done": "{amount} remboursé",
  "refund.refunded": "{amount} remboursé",
  "refund.amount": "Montant (jusqu'à {max})",
  "refund.reason": "Motif (facultatif)",
  "refund.cta": "Rembourser {amount}",
  "refund.ctaFull": "Rembourser {amount} en totalité",
  "refund.full": "Remboursé",
  "refund.partial": "Partiellement remboursé",
  "refund.open": "Rembourser",
  "errors.card_exists": "Vous avez déjà une carte.",
  "errors.issuing_unavailable": "Les cartes seront disponibles avec notre partenaire émetteur.",
  "errors.refund_exceeds_payment": "C'est plus que ce qui reste à rembourser.",
  "errors.already_refunded": "Ce paiement a déjà été remboursé en totalité.",
  "errors.payment_not_refundable": "Seuls les paiements réussis peuvent être remboursés.",
  "errors.sandbox_only": "Disponible en mode test uniquement.",
  "errors.invalid_amount": "Saisissez un montant valide.",
  "errors.already_contributed": "Vous avez déjà payé pour ce tour.",
  "errors.already_member": "Vous faites déjà partie de ce cercle.",
  "errors.already_paid_out": "La cagnotte de ce tour a déjà été versée.",
  "errors.campaign_closed": "Cette collecte est fermée.",
  "errors.circle_full": "Ce cercle est déjà complet.",
  "errors.circle_not_collecting": "Ce cercle ne collecte pas de cotisations en ce moment.",
  "errors.circle_started": "Ce cercle a déjà commencé.",
  "errors.immutable": "Cet enregistrement ne peut pas être modifié.",
  "errors.not_awaiting_card": "Ce paiement n'attend pas de coordonnées de carte.",
  "errors.not_awaiting_code": "Ce paiement n'attend pas de code.",
  "errors.not_enough_members": "Invitez au moins une autre personne avant de commencer.",
  "errors.not_sandbox": "Ce paiement n'est pas en mode test.",
  "errors.nothing_to_withdraw": "Il n'y a encore rien à retirer.",
  "errors.organizer_cannot_leave": "L'organisateur ne peut pas quitter le cercle.",
  "errors.payment_closed": "Ce lien de paiement n'est plus actif.",
  "errors.payment_succeeded": "Ce paiement est déjà effectué.",
  "errors.request_not_open": "Cette demande a déjà été payée ou annulée.",
  "errors.not_found": "Introuvable. Le lien est peut-être erroné, ou l'élément a été supprimé.",
  "errors.forbidden": "Vous n'avez pas accès à cette page.",
  "errors.payment_in_progress": "Ce paiement est en cours dans une autre fenêtre. Terminez-le là-bas, ou réessayez dans 30 minutes.",
  "errors.members_changed": "Quelqu'un vient de rejoindre ou de quitter le cercle. Vérifiez les membres et recommencez.",
  "errors.account_locked": "Trop de tentatives incorrectes sur ce compte. Attendez 15 minutes et réessayez.",
  "errors.too_many_lookups": "Trop de recherches. Attendez quelques minutes et réessayez.",
  "errors.idempotency_key_reused": "Cette demande a déjà été envoyée avec d'autres détails. Réessayez.",
  "errors.request_in_progress": "Votre tentative précédente est encore en cours. Réessayez dans un instant.",
  "errors.kind_locked": "Ce compte a des écritures. Créez plutôt un nouveau compte pour une carte ou un prêt.",
  "errors.invalid_date": "Saisissez une date valide.",
  "errors.duplicate_member": "Chaque membre ne peut apparaître qu'une fois.",
  "errors.card_declined": "La carte a été refusée par la banque. Réessayez ou choisissez un autre moyen.",
  "errors.operator_declined": "L'opérateur a refusé le paiement. Vérifiez votre solde mobile money.",
  "errors.too_big": "C'est trop élevé.",
  "errors.too_small": "C'est trop petit.",
  "errors.date_in_past": "Choisissez une date future.",
  "errors.https_required": "Utilisez une adresse qui commence par https://",
  "errors.invalid_format": "Vérifiez le format.",
  "errors.invalid_value": "Choisissez l'une des options.",
  "errors.card_over_daily_limit": "Au-delà de la limite quotidienne de la carte.",
  "errors.amount_too_big": "C'est plus qu'un seul paiement ne peut transporter (10 millions au maximum).",
  "errors.no_decimals": "Cette devise n'a pas de centimes. Saisissez un montant entier.",
  "audit.card.issued": "Carte émise",
  "audit.card.revealed": "Détails de la carte consultés",
  "audit.card.frozen": "Carte gelée",
  "audit.card.unfrozen": "Carte dégelée",
  "audit.card.cancelled": "Carte annulée",
  "audit.card.controls_changed": "Contrôles de la carte modifiés",
  "landing.p.plan.title": "Sachez ce que vous pouvez dépenser aujourd'hui",
  "landing.p.plan.body": "Réunissez banque, mobile money et cartes de crédit dans un seul plan. CafonaPay prévoit chaque facture et chaque paie, et vous prévient des semaines avant un manque.",
  "landing.p.card.title": "Une carte pour tous vos soldes",
  "landing.p.card.body": "Une Visa qui paie dans la bonne devise, avec plafonds, gel instantané et une protection qui empêche un achat d'entamer votre loyer.",
  "subnav.plan": "Plan",
  "subnav.receipts": "Reçus",
  "subnav.insights": "Analyses",
  "scope.label": "Dépenses de",
  "scope.me": "Moi",
  "rcpt.title": "Reçus",
  "rcpt.lede": "Chaque achat, détaillé : les reçus électroniques arrivent par courriel, ceux en papier par photo.",
  "rcpt.scan": "Scanner un reçu",
  "rcpt.scanHint": "Prenez une photo, ou choisissez une photo ou un PDF",
  "rcpt.reading": "Lecture du reçu…",
  "rcpt.paste": "Coller le texte d'un reçu",
  "rcpt.pasteHint": "Depuis un courriel ou un site web",
  "rcpt.pasteLabel": "Texte du reçu",
  "rcpt.itemize": "Détailler",
  "rcpt.email.title": "Transférez vos reçus électroniques",
  "rcpt.email.body": "Donnez cette adresse privée aux magasins qui offrent des reçus électroniques, ou faites-y transférer vos reçus depuis votre messagerie. Chacun est détaillé automatiquement.",
  "rcpt.email.bodyOrg": "Les membres de l'équipe transfèrent leurs reçus de dépenses à cette adresse. Chacun est détaillé dans les dépenses de l'organisation.",
  "rcpt.email.moneyOnly": "Seuls les propriétaires, administrateurs et trésoriers voient cette adresse. Vous pouvez quand même scanner ou coller des reçus.",
  "rcpt.email.how1": "À la caisse, demandez un reçu électronique et donnez cette adresse (Metro, Jean Coutu, Walmart, IKEA et bien d'autres l'offrent).",
  "rcpt.email.how2": "Dans Gmail ou Outlook, créez un filtre qui transfère à cette adresse les courriels contenant « reçu » ou « receipt » dans l'objet.",
  "rcpt.email.how3": "Gardez l'adresse privée : tout ce qui y est envoyé arrive dans vos reçus. Vous pouvez la remplacer à tout moment.",
  "rcpt.email.test": "Mode test : la réception par courriel est activée en production. Essayez avec un reçu d'exemple.",
  "rcpt.email.sample": "Envoyer un reçu d'exemple",
  "rcpt.rotate": "Remplacer cette adresse",
  "rcpt.rotated": "Nouvelle adresse prête. L'ancienne ne fonctionne plus.",
  "rcpt.list": "Vos reçus",
  "rcpt.toInsights": "Voir les analyses",
  "rcpt.empty": "Aucun reçu pour l'instant. Scannez-en un, collez-en un ou transférez un reçu électronique.",
  "rcpt.unknownMerchant": "Reçu",
  "rcpt.items": "{n} articles",
  "rcpt.needsReview": "à vérifier",
  "rcpt.matchedCard": "payé par carte",
  "rcpt.matchedPlan": "dans votre plan",
  "rcpt.status.ready": "Détaillé",
  "rcpt.status.needs_review": "À vérifier",
  "rcpt.status.processing": "Lecture",
  "rcpt.status.failed": "Illisible",
  "rcpt.reviewHint": "Certaines lignes de ce reçu n'ont pas pu être lues. Vérifiez les articles et le total, ou scannez une photo plus nette.",
  "rcpt.col.item": "Article",
  "rcpt.col.category": "Catégorie",
  "rcpt.col.qty": "Qté",
  "rcpt.col.total": "Montant",
  "rcpt.subtotal": "Sous-total",
  "rcpt.tax": "Taxes",
  "rcpt.total": "Total",
  "rcpt.source.email": "Reçu par courriel",
  "rcpt.source.scan": "Scanné",
  "rcpt.source.text": "Collé",
  "rcpt.source.manual": "Saisi à la main",
  "rcpt.readByAi": "lu par IA",
  "rcpt.readByRules": "lu automatiquement",
  "rcpt.notRead": "pas encore lu",
  "rcpt.imageAlt": "Photo du reçu",
  "rcpt.payment": "Paiement",
  "rcpt.matchedCardLong": "Associé à votre achat par carte CafonaPay.",
  "rcpt.matchedPlanLong": "Enregistré comme dépense dans votre plan financier.",
  "rcpt.orgNote": "Les reçus de l'organisation comptent dans ses analyses.",
  "rcpt.noAccounts": "Payé hors de CafonaPay ? Ajoutez le compte utilisé pour l'enregistrer.",
  "rcpt.addExpenseHint": "Payé hors de CafonaPay ? Enregistrez-le sur le compte utilisé.",
  "rcpt.addExpense": "Enregistrer comme dépense",
  "rcpt.expenseAdded": "Enregistré dans votre plan financier",
  "rcpt.delete": "Supprimer le reçu",
  "ins.title": "Analyses",
  "ins.lede": "Ce que vous achetez, ce que ça coûte et l'évolution des prix, d'après vos reçus détaillés.",
  "ins.range": "Période",
  "ins.range.week": "Semaine",
  "ins.range.month": "Mois",
  "ins.range.year": "Année",
  "ins.range.decade": "Décennie",
  "ins.prev": "Période précédente",
  "ins.next": "Période suivante",
  "ins.spent": "Dépensé en achats détaillés",
  "ins.vsPrev.week": "vs semaine dernière",
  "ins.vsPrev.month": "vs mois dernier",
  "ins.vsPrev.year": "vs année dernière",
  "ins.vsPrev.decade": "vs décennie précédente",
  "ins.counts": "{receipts} reçus · {items} articles",
  "ins.chartAria": "Dépenses par période. Sélectionnez une barre pour lire son total.",
  "ins.drillHint": "Sélectionnez une barre pour ouvrir cette période.",
  "ins.empty": "Aucun reçu détaillé sur cette période.",
  "ins.addReceipts": "Ajouter des reçus",
  "ins.products": "Ce que vous avez acheté",
  "ins.col.product": "Produit",
  "ins.col.qty": "Qté",
  "ins.col.avg": "Prix moyen",
  "ins.col.total": "Dépensé",
  "ins.times": "acheté {n} fois",
  "ins.categories": "Par catégorie",
  "ins.merchants": "Par magasin",
  "ins.visits": "{n} visites",
  "ins.p.total": "Dépensé, au total",
  "ins.p.purchases": "Nombre d'achats",
  "ins.p.price": "Prix payé dans le temps",
  "ins.p.priceAria": "Prix unitaire de {product} à chaque achat",
  "ins.p.history": "Tous les achats",
  "ins.p.where": "Où c'est le moins cher",
  "ins.p.cheapest": "Le moins cher",
  "ins.p.byYear": "Par année",
  "errors.unsupported": "Utilisez une photo (JPEG, PNG, WebP, HEIC) ou un PDF.",
  "errors.too_large": "Le fichier dépasse 10 Mo.",
  "errors.already_matched": "Ce reçu est déjà associé à un paiement.",
  "icat.produce": "Fruits et légumes",
  "icat.dairy_eggs": "Produits laitiers et œufs",
  "icat.meat_fish": "Viandes et poissons",
  "icat.bakery": "Boulangerie",
  "icat.pantry": "Épicerie",
  "icat.frozen": "Surgelés",
  "icat.beverages": "Boissons",
  "icat.alcohol": "Alcool",
  "icat.snacks": "Grignotines",
  "icat.household": "Entretien",
  "icat.personal_care": "Soins personnels",
  "icat.baby": "Bébé",
  "icat.pets": "Animaux",
  "icat.health": "Santé et pharmacie",
  "icat.clothing": "Vêtements",
  "icat.electronics": "Électronique",
  "icat.home": "Maison",
  "icat.fuel": "Carburant",
  "icat.restaurant": "Restaurants",
  "icat.services": "Services",
  "icat.other": "Autre",
};

export type Locale = "en" | "fr";
const dictionaries: Record<Locale, Record<MessageKey, string>> = { en, fr };
export const intlLocale = (l: Locale) => (l === "fr" ? "fr-CA" : "en-CA");

interface I18n {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  /** Translate an API error code ("invalid_phone") or pass free text through unchanged. */
  te: (codeOrText: string | undefined) => string | undefined;
}

const Ctx = createContext<I18n | null>(null);
const STORAGE_KEY = "cp.locale";

/** Translate outside React (build-time pre-render, head tags). */
export function translate(locale: Locale, key: MessageKey): string {
  return dictionaries[locale][key] ?? en[key];
}

function initialLocale(): Locale {
  // /fr/… addresses are French whatever the device says (one URL per language for search engines).
  try {
    if (location.pathname === "/fr" || location.pathname.startsWith("/fr/")) return "fr";
  } catch {
    /* no location (pre-render) */
  }
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "en" || saved === "fr") return saved;
  } catch {
    /* storage unavailable */
  }
  return typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("fr") ? "fr" : "en";
}

export function I18nProvider({ children, initial }: { children: ReactNode; initial?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(() => initial ?? initialLocale());
  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
  }, []);
  const t = useCallback<I18n["t"]>(
    (key, vars) => {
      let s: string = dictionaries[locale][key] ?? en[key];
      if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
      return s;
    },
    [locale],
  );
  const te = useCallback<I18n["te"]>(
    (codeOrText) => {
      if (!codeOrText) return codeOrText;
      const key = `errors.${codeOrText}` as MessageKey;
      return key in en ? t(key) : codeOrText;
    },
    [t],
  );
  // Screen readers and browsers read the page in the language it's shown in.
  useEffect(() => {
    document.documentElement.lang = locale === "fr" ? "fr-CA" : "en-CA";
  }, [locale]);
  const value = useMemo(() => ({ locale, setLocale, t, te }), [locale, setLocale, t, te]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useI18n outside I18nProvider");
  return ctx;
}
